import { usePainelRecolhivel } from '@/hooks/usePainelRecolhivel';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  SelectionMode,
  addEdge,
  reconnectEdge,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Connection,
  type Edge,
  type EdgeMouseHandler,
  type NodeChange,
  type NodePositionChange,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { AppLayout } from '@/components/layout/AppLayout';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import * as SelectPrimitive from '@radix-ui/react-select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/contexts/AuthContext';
import { exportElementAsPdf, exportElementAsPng } from '@/lib/exportCanvas';
import { buildNodesAndEdgesFromGrupos, offsetImportedNodes } from '@/lib/organogramaImportBuilder';
import type { ImportParseResult } from '@/lib/organogramaImport';
import { ImportOrganogramaDialog } from '@/components/organograma/ImportOrganogramaDialog';
import { TipoOrganogramaSelect } from '@/components/organograma/TipoOrganogramaSelect';
import { departamentoService, organogramasService, usuarioService } from '@/services/api';
import type { OrganogramaCanvasResponse } from '@/services/api';
import { projetotService } from '@/services/projetotApi';
import { ApiResponse, Cronograma, Departamento, Organograma, OrganogramaHistorico, OrganogramaPermissao, OrganogramaTipo, Usuario } from '@/types';
import {
  OrgMvpCardData,
  OrgMvpColumnData,
  OrgMvpColumnTitlePosition,
  OrgMvpFigure,
  OrgMvpFigureData,
  OrgMvpFigureKey,
  OrgMvpNode,
  OrgMvpNodeKind,
  OrgMvpShapeData,
  OrgMvpShapeKind,
  resolveColumnTitlePosition,
} from '@/components/organograma/organogramaTypes';
import { BibliotecaFiguras } from '@/components/figuras/BibliotecaFiguras';
import { figureCatalog, figureCatalogByKey } from '@/components/organograma/organogramaFigureCatalog';
import { organogramaTemplates, type OrganogramaTemplate } from '@/components/organograma/organogramaDemoData';
import { MvpAnnotationNode, MvpCardNode, MvpColumnNode, MvpEdge, MvpFigureNode, MvpShapeNode, MvpTableNode, MvpTextNode } from '@/components/organograma/OrganogramaNodes';
import { applyEdgeTipoStyle, mapApiEdgesToFlow, mapApiNodesToFlow, mapFlowEdgesToApi, mapFlowNodesToApi } from '@/components/organograma/organogramaMappers';
import {
  EDGE_TIPO_LABELS,
  EDGE_TIPOS,
  buildChildrenMap,
  countDirectReports,
  getCardIds,
  getDescendants,
  getEdgeTipo,
  isHierarchyEdge,
  nodeSizeOf,
  normalizeSearchText,
  wouldCreateCycle,
  type OrgMvpEdgeTipo,
} from '@/components/organograma/organogramaGraph';
import { applyAutoLayout } from '@/components/fluxograma/autoLayout';
import { CanvasContextMenu, type CtxItem, type CtxTarget } from '@/components/fluxograma/CanvasContextMenu';
import { AlignmentGuides } from '@/components/fluxograma/AlignmentGuides';
import { getHelperLines } from '@/components/fluxograma/helperLines';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { HISTORY_LIMIT, historySnapshotsEqual, makeHistorySnapshot, type OrgMvpHistorySnapshot } from '@/components/organograma/organogramaHistory';
import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignHorizontalDistributeCenter,
  AlignStartHorizontal,
  AlignStartVertical,
  AlignVerticalDistributeCenter,
  ArrowDownToLine,
  ArrowLeft,
  ArrowUpToLine,
  Building2,
  Circle,
  ImagePlus,
  LayoutTemplate,
  Shapes,
  Square,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Columns3,
  Download,
  FileText,
  Filter,
  Forklift,
  Hand,
  HelpCircle,
  History,
  PanelLeftClose,
  PanelLeftOpen,
  Link2,
  StickyNote,
  Type as TypeIcon,
  Maximize2,
  Scan,
  Minus,
  MousePointer2,
  Plus,
  Redo2,
  Save,
  Search,
  Settings2,
  Shield,
  Undo2,
  Unlink,
  Upload,
  UserRound,
  Wand2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

const colorPresets = ['#2563EB', '#059669', '#EA580C', '#7C3AED', '#DC2626', '#0F766E', '#000000', '#FFFFFF'];

const HOW_TO_STEPS = [
  'Use a aba "Adicionar" para inserir colunas, departamentos, pessoas, figuras, textos, anotações ou tabelas no canvas.',
  'Clique e arraste numa área vazia do canvas para selecionar vários itens de uma vez, ou use Ctrl/Cmd+A para selecionar tudo.',
  'Clique num item para abrir o painel de propriedades à direita e editar cor, texto, tipo de card e outros detalhes.',
  'Arraste a partir da borda de um card até outro para criar uma conexão de hierarquia entre eles.',
  'Na aba "Visual", organize o layout automaticamente, cole colunas próximas, enquadre o desenho ou entre em tela cheia.',
  'Salve com o botão "Salvar" ou Ctrl+S — o organograma também salva sozinho enquanto você edita.',
  'Na aba "Arquivo", exporte em PNG/PDF e consulte o histórico de versões salvas.',
];

type OrganogramaMetaForm = {
  nome: string;
  descricao: string;
  tipo: OrganogramaTipo;
  status: Organograma['status'];
};

type AlignMode = 'left' | 'center-h' | 'right' | 'top' | 'middle-v' | 'bottom';

const ALIGN_ACTIONS: Array<{ mode: AlignMode; label: string; icon: typeof AlignStartVertical }> = [
  { mode: 'left', label: 'Alinhar à esquerda', icon: AlignStartVertical },
  { mode: 'center-h', label: 'Alinhar ao centro', icon: AlignCenterVertical },
  { mode: 'right', label: 'Alinhar à direita', icon: AlignEndVertical },
  { mode: 'top', label: 'Alinhar ao topo', icon: AlignStartHorizontal },
  { mode: 'middle-v', label: 'Alinhar ao meio', icon: AlignCenterHorizontal },
  { mode: 'bottom', label: 'Alinhar à base', icon: AlignEndHorizontal },
];

const DISTRIBUTE_ACTIONS: Array<{ axis: 'horizontal' | 'vertical'; label: string; icon: typeof AlignStartVertical }> = [
  { axis: 'horizontal', label: 'Distribuir espaçamento horizontal', icon: AlignHorizontalDistributeCenter },
  { axis: 'vertical', label: 'Distribuir espaçamento vertical', icon: AlignVerticalDistributeCenter },
];

const COLUMN_TITLE_POSITION_OPTIONS: Array<{ value: OrgMvpColumnTitlePosition; label: string }> = [
  { value: 'left', label: 'Esquerda' },
  { value: 'top', label: 'Topo' },
  { value: 'bottom', label: 'Embaixo' },
];

const SHAPE_OPTIONS: Array<{ shape: OrgMvpShapeKind; label: string; size: { width: number; height: number } }> = [
  { shape: 'square', label: 'Quadrado', size: { width: 160, height: 160 } },
  { shape: 'circle', label: 'Círculo', size: { width: 160, height: 160 } },
  { shape: 'line', label: 'Linha', size: { width: 220, height: 24 } },
];

/** Fator do "+"/"−" do stepper de tamanho: proporcional para preservar a diferença
 *  relativa quando vários elementos de tamanhos diferentes estão selecionados. */
const SIZE_STEP_FACTOR = 1.1;

type PendingConnectionDraft = {
  sourceId: string;
  sourceHandle: string | null;
  handleType: 'source' | 'target' | null;
  clientX: number;
  clientY: number;
};

const newId = (prefix: string) => `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

const oppositeDir = (dir: string): 'top' | 'right' | 'bottom' | 'left' =>
  dir === 'top' ? 'bottom' : dir === 'bottom' ? 'top' : dir === 'left' ? 'right' : 'left';

// Alinhado com o whitelist do backend (`detectImageMimeType` só confirma JPEG/PNG/WEBP
// pelos magic bytes; SVG fica de fora por não ter assinatura binária confiável).

const DEFAULT_CARD_WIDTH = 220;
const DEFAULT_CARD_HEIGHT = 118;
const GRID_SIZE = 16;
const MIN_STROKE_WIDTH = 1;
const MAX_STROKE_WIDTH = 12;
const clampStrokeWidth = (value: number) =>
  Math.min(MAX_STROKE_WIDTH, Math.max(MIN_STROKE_WIDTH, Math.round(value)));
const MIN_NODE_SIZE = 36;
const MAX_NODE_SIZE = 2000;

const clampNodeSize = (value: number) => Math.min(MAX_NODE_SIZE, Math.max(MIN_NODE_SIZE, Math.round(value)));

/** Sem isso o nó recém-criado ficaria selecionado junto com o que já estava selecionado antes. */
const deselectAll = (list: OrgMvpNode[]) => list.map((node) => (node.selected ? { ...node, selected: false } : node));

// Escopado por organograma para não vazar referências (usuarioId/departamentoId)
// entre organogramas de empresas diferentes ao copiar/colar.
const getClipboardKey = (organogramaId: string) => `organograma:clipboard:${organogramaId}`;
const PASTE_OFFSET = 32;
const DUPLICATE_OFFSET = 40;

const oppositeHandleBySource: Record<string, string> = {
  top: 't-bottom',
  right: 't-left',
  bottom: 't-top',
  left: 't-right',
};

const oppositeSourceHandleBySource: Record<string, 'top' | 'right' | 'bottom' | 'left'> = {
  top: 'bottom',
  right: 'left',
  bottom: 'top',
  left: 'right',
};

const loadOrganogramaCanvas = async (organogramaId: string): Promise<ApiResponse<OrganogramaCanvasResponse | null>> => {
  const service = organogramasService as {
    getCanvas?: (id: string) => Promise<ApiResponse<OrganogramaCanvasResponse | null>>;
  };

  if (typeof service.getCanvas === 'function') {
    return service.getCanvas(organogramaId);
  }

  try {
    return await apiClient.get<ApiResponse<OrganogramaCanvasResponse>>(`/organogramas/${organogramaId}/canvas`);
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro ao buscar canvas do organograma',
      data: null,
    };
  }
};

const serializeCanvasSnapshot = (nodes: OrgMvpNode[], edges: Edge[]) =>
  JSON.stringify({
    nodes: mapFlowNodesToApi(nodes),
    edges: mapFlowEdgesToApi(edges),
  });

const isEditableTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
};

/** Troca ids client-side pelos ids persistidos devolvidos pelo backend. */
const rebaseIds = (
  nodesList: OrgMvpNode[],
  edgesList: Edge[],
  nodeMapping: Record<string, string>,
  edgeMapping: Record<string, string>,
) => {
  const mapNodeId = (id: string) => nodeMapping[id] || id;
  return {
    nodes: nodesList.map((node) => (nodeMapping[node.id] ? { ...node, id: nodeMapping[node.id] } : node)),
    edges: edgesList.map((edge) => {
      const nextId = edgeMapping[edge.id] || edge.id;
      const nextSource = mapNodeId(edge.source);
      const nextTarget = mapNodeId(edge.target);
      if (nextId === edge.id && nextSource === edge.source && nextTarget === edge.target) return edge;
      return { ...edge, id: nextId, source: nextSource, target: nextTarget };
    }),
  };
};

/** Copia nodes/edges do modelo com ids novos — o modelo é ponto de partida, não vínculo. */
const instantiateTemplate = (template: OrganogramaTemplate) => {
  const idByTemplateId = new Map(template.nodes.map((node) => [node.id, newId(node.type || 'node')]));
  return {
    nodes: template.nodes.map((node) => ({ ...node, id: idByTemplateId.get(node.id)!, selected: false })),
    edges: template.edges.map((edge) => ({
      ...edge,
      id: newId('edge'),
      source: idByTemplateId.get(edge.source) || edge.source,
      target: idByTemplateId.get(edge.target) || edge.target,
    })),
  };
};

const AUTO_SAVE_DELAY_MS = 2000;
const AUTO_SAVE_MAX_FAILURES = 3;

function OrganogramaMvpInner() {
  const { id: organogramaId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [nodes, setNodes, onNodesChange] = useNodesState<OrgMvpNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [isLoadingCanvas, setIsLoadingCanvas] = useState(false);
  const [isSavingCanvas, setIsSavingCanvas] = useState(false);
  const [canEditCanvas, setCanEditCanvas] = useState(true);
  const [currentOrganograma, setCurrentOrganograma] = useState<Organograma | null>(null);
  const [organogramaUpdatedAt, setOrganogramaUpdatedAt] = useState<string | undefined>(undefined);
  const [organogramaNome, setOrganogramaNome] = useState('Organograma');
  const [dirty, setDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [cronogramas, setCronogramas] = useState<Cronograma[]>([]);
  const [historico, setHistorico] = useState<OrganogramaHistorico[]>([]);
  const [permissoes, setPermissoes] = useState<OrganogramaPermissao[]>([]);
  const [isHistoricoDialogOpen, setIsHistoricoDialogOpen] = useState(false);
  const [isPermissoesDialogOpen, setIsPermissoesDialogOpen] = useState(false);
  const [isMetaDialogOpen, setIsMetaDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [metaForm, setMetaForm] = useState<OrganogramaMetaForm>({
    nome: '',
    descricao: '',
    tipo: 'customizado',
    status: 'rascunho',
  });
  /** Só mostra o erro de nome vazio depois de uma tentativa de salvar. */
  const [tentouSalvarMeta, setTentouSalvarMeta] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [ctxMenu, setCtxMenu] = useState<CtxTarget | null>(null);
  const [helperLineH, setHelperLineH] = useState<number | undefined>(undefined);
  const [helperLineV, setHelperLineV] = useState<number | undefined>(undefined);
  const [isRenamingTitle, setIsRenamingTitle] = useState(false);
  // Painel "Adicionar": recolhível, com a escolha salva no navegador (no celular segue como antes).
  const { aberto: paletteOpen, alternar: alternarPaleta } = usePainelRecolhivel('kora:organograma:paleta-recolhida', {
    fecharNoCelular: false,
  });
  const [howToOpen, setHowToOpen] = useState(false);
  // Clique no ícone de um card: abre o painel direto no seletor de ícone daquele card, em vez
  // do formulário de propriedades normal. `at` serve para o catálogo rolar até o ícone em uso.
  const [figureFocus, setFigureFocus] = useState<{ nodeId: string; at: number } | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [undoStack, setUndoStack] = useState<OrgMvpHistorySnapshot[]>([]);
  const [redoStack, setRedoStack] = useState<OrgMvpHistorySnapshot[]>([]);
  const [pendingConnectionDraft, setPendingConnectionDraft] = useState<PendingConnectionDraft | null>(null);
  const [pendingEdgeDeleteId, setPendingEdgeDeleteId] = useState<string | null>(null);
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());
  const [departmentFilterIds, setDepartmentFilterIds] = useState<Set<string>>(new Set());
  const [hideFiltered, setHideFiltered] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchIndex, setSearchIndex] = useState(0);
  const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null);
  const [isLayoutConfirmOpen, setIsLayoutConfirmOpen] = useState(false);
  const [autoSaveDisabled, setAutoSaveDisabled] = useState(false);
  const [saveConflictOpen, setSaveConflictOpen] = useState(false);
  const [saveBlockedByConflict, setSaveBlockedByConflict] = useState(false);
  const [isDraggingNode, setIsDraggingNode] = useState(false);
  const [mobilePaletteOpen, setMobilePaletteOpen] = useState(false);
  const [mobilePropsOpen, setMobilePropsOpen] = useState(false);
  const [canvasHeight, setCanvasHeight] = useState(760);
  const canvasResizeRef = useRef<{ startY: number; startHeight: number } | null>(null);
  const [propertiesPanelWidth, setPropertiesPanelWidth] = useState(300);
  const propertiesResizeRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const [interactionMode, setInteractionMode] = useState<'select' | 'pan'>('select');
  // Último tamanho de card definido nesta sessão de edição (arraste ou campos numéricos).
  // Só vive em memória — não persiste no banco.
  const [lastCardSize, setLastCardSize] = useState({ width: DEFAULT_CARD_WIDTH, height: DEFAULT_CARD_HEIGHT });
  const [templateChooserOpen, setTemplateChooserOpen] = useState(false);
  const templateChooserShownRef = useRef(false);
  const loadedOrganogramaIdRef = useRef<string | null>(null);
  // Modelo escolhido no diálogo de criação (tela de listagem), entregue pela query string.
  const requestedTemplateKeyRef = useRef<string | null>(searchParams.get('template'));
  const savingRef = useRef(false);
  const autoSaveFailuresRef = useRef(0);
  const nodesRef = useRef<OrgMvpNode[]>([]);
  const edgesRef = useRef<Edge[]>([]);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { fitView, screenToFlowPosition, setCenter, getZoom } = useReactFlow();
  const workspaceRef = useRef<HTMLDivElement | null>(null);
  const exportAreaRef = useRef<HTMLDivElement | null>(null);
  const dragStartSnapshotRef = useRef<OrgMvpHistorySnapshot | null>(null);
  const pendingConnectSourceRef = useRef<{ sourceId: string; sourceHandle: string | null; handleType: 'source' | 'target' | null } | null>(null);
  const justConnectedRef = useRef(false);
  const baselineSnapshotRef = useRef<string>('');
  const nodeTypes = useMemo(
    () => ({
      card: MvpCardNode,
      column: MvpColumnNode,
      figure: MvpFigureNode,
      text: MvpTextNode,
      table: MvpTableNode,
      annotation: MvpAnnotationNode,
      shape: MvpShapeNode,
    }),
    [],
  );
  // Mesmo componente nas 3 chaves: quem decide o formato do traçado é o `type` de cada edge
  // (controlado pelo toggle "Forma da linha"), o `MvpEdge` só lê esse valor internamente.
  const edgeTypes = useMemo(
    () => ({
      default: MvpEdge,
      smoothstep: MvpEdge,
      straight: MvpEdge,
    }),
    [],
  );
  const canManageOrganograma = user?.tipo === 'superadmin' || user?.tipo === 'admin';

  // Um mesmo organograma só é carregado uma vez por montagem. Sem isso, um segundo
  // carregamento (o StrictMode em dev remonta o efeito; em produção o `user` do AuthContext
  // chega depois da validação do token) voltava com o canvas ainda vazio no servidor e
  // apagava o modelo recém-aplicado, que só é gravado no auto-save 2s depois. A marcação é
  // síncrona de propósito: as duas chamadas concorrem antes de qualquer re-render.
  const reloadCanvas = useCallback(async ({ force = false }: { force?: boolean } = {}) => {
    if (!organogramaId) return;
    if (!force && loadedOrganogramaIdRef.current === organogramaId) return;
    loadedOrganogramaIdRef.current = organogramaId;
    setIsLoadingCanvas(true);
    try {
      const [canvasResult, organogramaResult] = await Promise.all([
        loadOrganogramaCanvas(organogramaId),
        organogramasService.getById(organogramaId),
      ]);

      if (organogramaResult.success && organogramaResult.data) {
        setCurrentOrganograma(organogramaResult.data);
        setOrganogramaNome(organogramaResult.data.nome);
        setMetaForm({
          nome: organogramaResult.data.nome,
          descricao: organogramaResult.data.descricao || '',
          tipo: organogramaResult.data.tipo,
          status: organogramaResult.data.status,
        });
      }
      if (organogramaResult.success && organogramaResult.data?.empresa_id) {
        const [usersResult, departamentosResult, projetosTResult] = await Promise.all([
          usuarioService.getByEmpresa(organogramaResult.data.empresa_id),
          departamentoService.getByEmpresa(organogramaResult.data.empresa_id),
          projetotService.getAll(undefined, organogramaResult.data.empresa_id),
        ]);
        if (usersResult.success) setUsuarios(usersResult.data || []);
        if (departamentosResult.success) setDepartamentos(departamentosResult.data || []);
        // Desde a unificação (2026-09-23) todo registro de `cronogramas` é um Projeto — basta
        // GET /projetot (GET /cronogramas só traz status_projeto NULL, que não existe mais).
        if (projetosTResult.success) setCronogramas(projetosTResult.data || []);
      }

      if (!canvasResult.success || !canvasResult.data) {
        toast.error(canvasResult.error || 'Erro ao carregar canvas');
        return;
      }

      const loadedNodes = mapApiNodesToFlow(canvasResult.data.nodes || []);
      const nodeIds = new Set(loadedNodes.map((node) => node.id));
      const loadedEdges = mapApiEdgesToFlow(canvasResult.data.edges || []).filter(
        (edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target),
      );

      setCanEditCanvas(canvasResult.data.canEdit !== false);
      setOrganogramaUpdatedAt(canvasResult.data.organograma?.updatedAt);

      setNodes(loadedNodes);
      setEdges(loadedEdges);
      setUndoStack([]);
      setRedoStack([]);
      setCollapsedIds(new Set());
      setDepartmentFilterIds(new Set());
      baselineSnapshotRef.current = serializeCanvasSnapshot(loadedNodes, loadedEdges);
      setDirty(false);
      setLastSavedAt(null);
      setAutoSaveDisabled(false);
      setSaveBlockedByConflict(false);
      setSaveConflictOpen(false);

      // Modelo de partida: só depois de confirmar pela resposta do backend que o canvas
      // está mesmo vazio. É aplicado aqui dentro (e não num efeito separado) para que um
      // segundo carregamento não possa sobrescrever o modelo recém-aplicado.
      if (
        canvasResult.data.canEdit !== false
        && loadedNodes.length === 0
        && loadedEdges.length === 0
        && !templateChooserShownRef.current
      ) {
        templateChooserShownRef.current = true;
        const requestedTemplate = organogramaTemplates.find((item) => item.key === requestedTemplateKeyRef.current);
        requestedTemplateKeyRef.current = null;
        if (requestedTemplate) {
          const instantiated = instantiateTemplate(requestedTemplate);
          setNodes(instantiated.nodes);
          setEdges(instantiated.edges);
          setTimeout(() => fitView({ duration: 300, padding: 0.16 }), 60);
          toast.success(`Modelo "${requestedTemplate.nome}" aplicado — edite à vontade`);
        } else {
          setTemplateChooserOpen(true);
        }
      }
    } catch (error) {
      console.error('Falha inesperada ao carregar canvas do organograma:', error);
      toast.error('Falha inesperada ao carregar canvas. Verifique o deploy/frontend em produção.');
    } finally {
      setIsLoadingCanvas(false);
    }
  }, [fitView, organogramaId, setEdges, setNodes]);

  useEffect(() => {
    reloadCanvas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organogramaId]);

  const handleReloadAfterConflict = useCallback(async () => {
    setSaveConflictOpen(false);
    await reloadCanvas({ force: true });
    toast.success('Organograma recarregado com a versão mais recente');
  }, [reloadCanvas]);

  useEffect(() => {
    nodesRef.current = nodes;
    edgesRef.current = edges;
    if (isLoadingCanvas) return;
    const currentSnapshot = serializeCanvasSnapshot(nodes, edges);
    setDirty(currentSnapshot !== baselineSnapshotRef.current);
  }, [edges, isLoadingCanvas, nodes]);

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const pushUndoSnapshot = useCallback((snapshot: OrgMvpHistorySnapshot) => {
    setUndoStack((current) => [...current.slice(-(HISTORY_LIMIT - 1)), snapshot]);
    setRedoStack([]);
  }, []);

  const commitHistory = useCallback(() => {
    pushUndoSnapshot(makeHistorySnapshot(nodes, edges, selectedNodeId));
  }, [edges, nodes, pushUndoSnapshot, selectedNodeId]);

  const undo = useCallback(() => {
    setUndoStack((current) => {
      const previous = current[current.length - 1];
      if (!previous) return current;

      setRedoStack((redos) => [
        ...redos.slice(-(HISTORY_LIMIT - 1)),
        makeHistorySnapshot(nodes, edges, selectedNodeId),
      ]);
      setNodes(previous.nodes);
      setEdges(previous.edges);
      setSelectedNodeId(previous.selectedNodeId);

      return current.slice(0, -1);
    });
  }, [edges, nodes, selectedNodeId, setEdges, setNodes]);

  const redo = useCallback(() => {
    setRedoStack((current) => {
      const next = current[current.length - 1];
      if (!next) return current;

      setUndoStack((undos) => [
        ...undos.slice(-(HISTORY_LIMIT - 1)),
        makeHistorySnapshot(nodes, edges, selectedNodeId),
      ]);
      setNodes(next.nodes);
      setEdges(next.edges);
      setSelectedNodeId(next.selectedNodeId);

      return current.slice(0, -1);
    });
  }, [edges, nodes, selectedNodeId, setEdges, setNodes]);

  const selectedNode = useMemo(
    () => nodes.find((node) => node.id === selectedNodeId) || null,
    [nodes, selectedNodeId],
  );
  const selectedEdge = useMemo(
    () => edges.find((edge) => edge.id === selectedEdgeId) || null,
    [edges, selectedEdgeId],
  );
  const selectedCount = useMemo(() => nodes.filter((node) => node.selected).length, [nodes]);

  const sizeStepperTargets = useMemo(() => {
    const marked = nodes.filter((node) => node.selected);
    if (marked.length > 0) return marked;
    return selectedNode ? [selectedNode] : [];
  }, [nodes, selectedNode]);
  const sizeStepperCount = sizeStepperTargets.length;
  /** `null` = larguras diferentes entre os selecionados (o campo aparece vazio). */
  const selectedSizeWidth = useMemo(() => {
    if (sizeStepperTargets.length === 0) return null;
    const widths = sizeStepperTargets.map((node) => Math.round(nodeSizeOf(node).width));
    return widths.every((value) => value === widths[0]) ? widths[0] : null;
  }, [sizeStepperTargets]);

  const handleDataChange = useCallback(
    (nodeId: string, updates: Partial<OrgMvpCardData | OrgMvpColumnData>) => {
      commitHistory();
      setNodes((current) =>
        current.map((node) => {
          if (node.id !== nodeId) return node;
          return { ...node, data: { ...node.data, ...updates } };
        }),
      );
    },
    [commitHistory, setNodes],
  );

  const handleNodeSizeChange = useCallback(
    (nodeId: string, size: { width?: number; height?: number }) => {
      if (!canEditCanvas) return;
      commitHistory();
      setNodes((current) =>
        current.map((node) => {
          if (node.id !== nodeId) return node;
          const currentSize = nodeSizeOf(node);
          const width = clampNodeSize(size.width ?? currentSize.width);
          const height = clampNodeSize(size.height ?? currentSize.height);
          if (node.type === 'card') setLastCardSize({ width, height });
          return { ...node, width, height, style: { ...node.style, width, height } };
        }),
      );
    },
    [canEditCanvas, commitHistory, setNodes],
  );

  const changeNodeLayer = useCallback(
    (direction: 'front' | 'back') => {
      if (!canEditCanvas || !selectedNodeId) return;
      const zIndexes = nodesRef.current.map((node) => node.zIndex ?? 0);
      const target = direction === 'front' ? Math.max(...zIndexes) + 1 : Math.min(...zIndexes) - 1;
      commitHistory();
      setNodes((current) =>
        current.map((node) => (node.id === selectedNodeId ? { ...node, zIndex: target } : node)),
      );
    },
    [canEditCanvas, commitHistory, selectedNodeId, setNodes],
  );

  const applyPositions = useCallback(
    (nextPositionById: Map<string, { x: number; y: number }>) => {
      commitHistory();
      setNodes((current) =>
        current.map((node) => {
          const nextPosition = nextPositionById.get(node.id);
          return nextPosition ? { ...node, position: nextPosition } : node;
        }),
      );
    },
    [commitHistory, setNodes],
  );

  const alignSelected = useCallback(
    (mode: AlignMode) => {
      if (!canEditCanvas) return;
      const boxes = nodesRef.current
        .filter((node) => node.selected)
        .map((node) => ({ node, ...nodeSizeOf(node) }));
      if (boxes.length < 2) return;

      const minX = Math.min(...boxes.map((box) => box.node.position.x));
      const maxX = Math.max(...boxes.map((box) => box.node.position.x + box.width));
      const minY = Math.min(...boxes.map((box) => box.node.position.y));
      const maxY = Math.max(...boxes.map((box) => box.node.position.y + box.height));
      const centerX = (minX + maxX) / 2;
      const middleY = (minY + maxY) / 2;

      const nextPositionById = new Map<string, { x: number; y: number }>();
      boxes.forEach(({ node, width, height }) => {
        let { x, y } = node.position;
        if (mode === 'left') x = minX;
        else if (mode === 'right') x = maxX - width;
        else if (mode === 'center-h') x = centerX - width / 2;
        else if (mode === 'top') y = minY;
        else if (mode === 'bottom') y = maxY - height;
        else if (mode === 'middle-v') y = middleY - height / 2;
        nextPositionById.set(node.id, { x: Math.round(x), y: Math.round(y) });
      });
      applyPositions(nextPositionById);
    },
    [applyPositions, canEditCanvas],
  );

  const distributeSelected = useCallback(
    (axis: 'horizontal' | 'vertical') => {
      if (!canEditCanvas) return;
      const isHorizontal = axis === 'horizontal';
      const boxes = nodesRef.current
        .filter((node) => node.selected)
        .map((node) => ({ node, ...nodeSizeOf(node) }))
        .sort((a, b) =>
          isHorizontal ? a.node.position.x - b.node.position.x : a.node.position.y - b.node.position.y,
        );
      if (boxes.length < 3) return;

      const sizeOf = (box: (typeof boxes)[number]) => (isHorizontal ? box.width : box.height);
      const startOf = (box: (typeof boxes)[number]) => (isHorizontal ? box.node.position.x : box.node.position.y);
      const first = boxes[0];
      const last = boxes[boxes.length - 1];
      const span = startOf(last) + sizeOf(last) - startOf(first);
      const occupied = boxes.reduce((total, box) => total + sizeOf(box), 0);
      const gap = (span - occupied) / (boxes.length - 1);

      let cursor = startOf(first);
      const nextPositionById = new Map<string, { x: number; y: number }>();
      boxes.forEach((box) => {
        nextPositionById.set(box.node.id, {
          x: isHorizontal ? Math.round(cursor) : box.node.position.x,
          y: isHorizontal ? box.node.position.y : Math.round(cursor),
        });
        cursor += sizeOf(box) + gap;
      });
      applyPositions(nextPositionById);
    },
    [applyPositions, canEditCanvas],
  );

  // Preserva a seleção quando o clique que encerra a edição inline do título da
  // coluna cai no canvas vazio (o React Flow trata isso como deselect de pane).
  const selectedNodeIdRef = useRef<string | null>(selectedNodeId);
  useEffect(() => {
    selectedNodeIdRef.current = selectedNodeId;
  }, [selectedNodeId]);

  // true durante um drag box-select (Shift+arraste) — nesse caso um deselect real pode
  // acontecer logo após editar um título, e não deve ser tratado como o clique de confirmação.
  const isBoxSelectingRef = useRef(false);
  // Janela de tolerância: o clique que confirma a edição pode cair no canvas vazio e disparar
  // um deselect de pane. Enquanto `lastEditedRef` estiver armado (400ms), esse deselect é
  // interpretado como "confirmação de edição" e a seleção é restaurada em vez de perdida.
  const lastEditedRef = useRef<{ id: string; timer: ReturnType<typeof setTimeout> } | null>(null);

  // Wired em `data.onEditEnd` de todo tipo de nó via `decoratedNodes`, mas hoje só
  // MvpColumnNode (título inline editável no canvas) realmente o dispara.
  const handleNodeEditEnd = useCallback((nodeId: string) => {
    if (selectedNodeIdRef.current !== nodeId) return;
    if (lastEditedRef.current) clearTimeout(lastEditedRef.current.timer);
    const timer = setTimeout(() => {
      lastEditedRef.current = null;
    }, 400);
    lastEditedRef.current = { id: nodeId, timer };
  }, []);

  const handleSelectionChange = useCallback(
    ({ nodes: sel, edges: selEdges }: { nodes: OrgMvpNode[]; edges: Edge[] }) => {
      const pending = lastEditedRef.current;
      const selIds = sel.map((n) => n.id);

      // Deselect real (não eco) durante a janela de tolerância: restaura a seleção anterior.
      // A checagem em `nodesRef.current` evita restaurar um nó que já foi excluído nesse meio-tempo.
      if (
        sel.length === 0
        && selEdges.length === 0
        && pending
        && !isBoxSelectingRef.current
        && nodesRef.current.some((n) => n.id === pending.id)
      ) {
        clearTimeout(pending.timer);
        lastEditedRef.current = null;
        setSelectedNodeId(pending.id);
        setSelectedEdgeId(null);
        setNodes((prev) => prev.map((n) => ({ ...n, selected: n.id === pending.id })));
        return;
      }

      // Echo: o próprio commit do título (setNodes em handleDataChange) faz o React Flow
      // re-disparar a seleção com o mesmo nó — não limpa `pending` para manter a proteção armada.
      if (pending && selEdges.length === 0 && selIds.length === 1 && selIds[0] === pending.id) {
        setSelectedNodeId(selIds[0]);
        setSelectedEdgeId(null);
        return;
      }

      if (pending) {
        clearTimeout(pending.timer);
        lastEditedRef.current = null;
      }
      setSelectedNodeId(selIds[0] || null);
      setSelectedEdgeId(selEdges[0]?.id || null);
    },
    [setNodes],
  );

  const usuariosById = useMemo(() => new Map(usuarios.map((usuario) => [usuario.id, usuario])), [usuarios]);
  const departamentosById = useMemo(() => new Map(departamentos.map((dep) => [dep.id, dep])), [departamentos]);

  const cardIds = useMemo(() => getCardIds(nodes), [nodes]);
  const childrenMap = useMemo(() => buildChildrenMap(edges, cardIds), [edges, cardIds]);
  const parentsMap = useMemo(() => {
    const map = new Map<string, string[]>();
    edges.forEach((edge) => {
      if (!isHierarchyEdge(edge, cardIds)) return;
      map.set(edge.target, [...(map.get(edge.target) || []), edge.source]);
    });
    return map;
  }, [edges, cardIds]);
  const directReportsMap = useMemo(() => countDirectReports(edges, cardIds), [edges, cardIds]);

  const hiddenByCollapse = useMemo(() => {
    const hidden = new Set<string>();
    collapsedIds.forEach((id) => {
      getDescendants(id, childrenMap).forEach((descendantId) => hidden.add(descendantId));
    });
    return hidden;
  }, [childrenMap, collapsedIds]);

  const filterActive = departmentFilterIds.size > 0;

  const dimmedNodeIds = useMemo(() => {
    if (!filterActive) return new Set<string>();
    const dimmed = new Set<string>();
    nodes.forEach((node) => {
      if (node.type !== 'card') return;
      const data = node.data as OrgMvpCardData;
      const usuario = data.usuarioId ? usuariosById.get(data.usuarioId) : undefined;
      const nodeDepartamentoId = data.departamentoId || usuario?.departamento_id || null;
      if (!nodeDepartamentoId || !departmentFilterIds.has(nodeDepartamentoId)) {
        dimmed.add(node.id);
      }
    });
    return dimmed;
  }, [departmentFilterIds, filterActive, nodes, usuariosById]);

  const hiddenNodeIds = useMemo(() => {
    if (!filterActive || !hideFiltered) return hiddenByCollapse;
    const hidden = new Set(hiddenByCollapse);
    dimmedNodeIds.forEach((id) => hidden.add(id));
    return hidden;
  }, [dimmedNodeIds, filterActive, hiddenByCollapse, hideFiltered]);

  const handleFigureClick = useCallback((nodeId: string) => {
    // O clique na figura não propaga para o React Flow, então a seleção é feita aqui —
    // tanto o id quanto a flag `selected` dos nós, para o canvas refletir o mesmo estado.
    setSelectedNodeId(nodeId);
    setSelectedEdgeId(null);
    setNodes((current) => current.map((node) => (
      node.selected === (node.id === nodeId) ? node : { ...node, selected: node.id === nodeId }
    )));
    setFigureFocus({ nodeId, at: Date.now() });
  }, [setNodes]);

  /** Clique em qualquer outra parte do card (ou no canvas) volta ao painel normal. */
  const clearFigureFocus = useCallback(() => setFigureFocus(null), []);

  const handleToggleCollapse = useCallback((nodeId: string) => {
    setCollapsedIds((current) => {
      const next = new Set(current);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  }, []);

  const decoratedNodes = useMemo(
    () =>
      nodes.map((node) => {
        const base = {
          ...node,
          hidden: hiddenNodeIds.has(node.id),
          data: {
            ...node.data,
            onChange: handleDataChange,
            onEditEnd: handleNodeEditEnd,
          },
        };
        if (node.type !== 'card') return base;

        const data = node.data as OrgMvpCardData;
        const usuario = data.usuarioId ? usuariosById.get(data.usuarioId) : undefined;
        const nodeDepartamentoId = data.departamentoId || usuario?.departamento_id || null;
        const departamento = nodeDepartamentoId ? departamentosById.get(nodeDepartamentoId) : undefined;
        const isCollapsed = collapsedIds.has(node.id);
        return {
          ...base,
          data: {
            ...base.data,
            directReports: directReportsMap.get(node.id) || 0,
            hasChildren: (childrenMap.get(node.id) || []).length > 0,
            isCollapsed,
            collapsedCount: isCollapsed ? getDescendants(node.id, childrenMap).size : 0,
            onToggleCollapse: handleToggleCollapse,
            onFigureClick: canEditCanvas ? handleFigureClick : undefined,
            quickView: {
              nome: data.label,
              cargo: data.subtitle || null,
              departamento: departamento?.nome || null,
              email: usuario?.email || null,
            },
            highlighted: highlightedNodeId === node.id,
            dimmed: dimmedNodeIds.has(node.id) && !(filterActive && hideFiltered),
          },
        };
      }),
    [
      canEditCanvas,
      childrenMap,
      collapsedIds,
      departamentosById,
      dimmedNodeIds,
      directReportsMap,
      filterActive,
      handleDataChange,
      handleFigureClick,
      handleNodeEditEnd,
      handleToggleCollapse,
      hiddenNodeIds,
      hideFiltered,
      highlightedNodeId,
      nodes,
      usuariosById,
    ],
  );

  /** Insere um novo card no meio da linha clicada, dividindo a conexão original em duas
   *  (pedido do Lucas, 08/09 — "coloque um + no meio da linha"). Reaproveita a direção da
   *  ponta de origem da linha original pras duas novas, pra manter o mesmo sentido visual. */
  const handleInsertOnEdge = useCallback(
    (edgeId: string, midX: number, midY: number) => {
      if (!canEditCanvas) return;
      const edge = edges.find((item) => item.id === edgeId);
      if (!edge) return;
      commitHistory();
      const id = newId('card');
      const { width, height } = lastCardSize;
      const sourceDir = (edge.sourceHandle as 'top' | 'right' | 'bottom' | 'left' | null) || 'bottom';
      setNodes((current) => [
        ...deselectAll(current),
        {
          id,
          type: 'card',
          position: { x: midX - width / 2, y: midY - height / 2 },
          width,
          height,
          zIndex: 10,
          selected: true,
          data: {
            kind: 'person',
            label: 'Nova pessoa',
            subtitle: 'Cargo ou função',
            description: '',
            sourceType: 'ORG_ONLY',
            usuarioId: null,
            departamentoId: null,
            color: '#2563EB',
            textColor: '#0F172A',
            figure: { type: 'catalog', key: 'person' },
          },
          style: { width, height },
        },
      ]);
      setEdges((current) => [
        ...current.filter((item) => item.id !== edgeId),
        {
          id: newId('edge'),
          source: edge.source,
          sourceHandle: edge.sourceHandle,
          target: id,
          targetHandle: `t-${oppositeDir(sourceDir)}`,
          type: edge.type,
          style: edge.style,
          data: edge.data,
        },
        {
          id: newId('edge'),
          source: id,
          sourceHandle: sourceDir,
          target: edge.target,
          targetHandle: edge.targetHandle,
          type: edge.type,
          style: edge.style,
          data: edge.data,
        },
      ]);
      setSelectedNodeId(id);
    },
    [canEditCanvas, commitHistory, edges, lastCardSize, setEdges, setNodes],
  );

  const decoratedEdges = useMemo(
    () =>
      edges.map((edge) => {
        const styled = applyEdgeTipoStyle(edge);
        const hidden = hiddenNodeIds.has(edge.source) || hiddenNodeIds.has(edge.target);
        const dim = dimmedNodeIds.has(edge.source) || dimmedNodeIds.has(edge.target);
        const baseStyle = dim ? { ...styled.style, opacity: 0.2 } : styled.style;
        const baseWidth = typeof baseStyle?.strokeWidth === 'number' ? baseStyle.strokeWidth : 2.2;
        return {
          ...styled,
          hidden,
          zIndex: edge.selected ? 6 : 5,
          // Faixa invisível de clique bem mais larga que a linha: sem isso só acerta
          // o painel de propriedades quem clica exatamente em cima do traço.
          interactionWidth: 26,
          data: {
            ...styled.data,
            onInsertNode: handleInsertOnEdge,
            readonly: !canEditCanvas,
          },
          style: edge.selected
            ? {
                ...baseStyle,
                strokeWidth: baseWidth + 1.8,
                filter: 'drop-shadow(0 0 5px rgba(15, 23, 42, 0.45))',
              }
            : baseStyle,
        };
      }),
    [canEditCanvas, dimmedNodeIds, edges, handleInsertOnEdge, hiddenNodeIds],
  );

  const searchMatches = useMemo(() => {
    const query = normalizeSearchText(searchQuery.trim());
    if (!query) return [] as OrgMvpNode[];
    return nodes.filter((node) => {
      if (node.type === 'column') {
        const data = node.data as OrgMvpColumnData;
        return normalizeSearchText(data.title || '').includes(query);
      }
      if (node.type === 'figure') {
        const data = node.data as OrgMvpFigureData;
        return normalizeSearchText(data.label || '').includes(query);
      }
      const data = node.data as OrgMvpCardData;
      return (
        normalizeSearchText(data.label || '').includes(query)
        || normalizeSearchText(data.subtitle || '').includes(query)
      );
    });
  }, [nodes, searchQuery]);

  useEffect(() => {
    setSearchIndex(0);
  }, [searchQuery]);

  const focusSearchResult = useCallback(
    (index: number) => {
      const node = searchMatches[index];
      if (!node) return;

      // Expande ancestrais colapsados para o resultado ficar visível
      setCollapsedIds((current) => {
        if (current.size === 0) return current;
        const next = new Set(current);
        const seen = new Set<string>();
        const stack = [node.id];
        while (stack.length > 0) {
          const currentId = stack.pop();
          if (!currentId) continue;
          (parentsMap.get(currentId) || []).forEach((parentId) => {
            if (seen.has(parentId)) return;
            seen.add(parentId);
            next.delete(parentId);
            stack.push(parentId);
          });
        }
        return next;
      });

      const { width, height } = nodeSizeOf(node);
      setCenter(node.position.x + width / 2, node.position.y + height / 2, { zoom: 1.1, duration: 400 });
      setHighlightedNodeId(node.id);
      if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
      highlightTimeoutRef.current = setTimeout(() => setHighlightedNodeId(null), 2400);
    },
    [parentsMap, searchMatches, setCenter],
  );

  const handleSearchNavigate = useCallback(
    (direction: 1 | -1) => {
      if (searchMatches.length === 0) return;
      const nextIndex = (searchIndex + direction + searchMatches.length) % searchMatches.length;
      setSearchIndex(nextIndex);
      focusSearchResult(nextIndex);
    },
    [focusSearchResult, searchIndex, searchMatches.length],
  );

  const handleCanvasResizeStart = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      canvasResizeRef.current = { startY: event.clientY, startHeight: canvasHeight };

      const onMouseMove = (moveEvent: MouseEvent) => {
        if (!canvasResizeRef.current) return;
        const delta = moveEvent.clientY - canvasResizeRef.current.startY;
        const next = Math.min(1600, Math.max(400, canvasResizeRef.current.startHeight + delta));
        setCanvasHeight(next);
      };
      const onMouseUp = () => {
        canvasResizeRef.current = null;
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      };
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    },
    [canvasHeight],
  );

  const handlePropertiesResizeStart = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      propertiesResizeRef.current = { startX: event.clientX, startWidth: propertiesPanelWidth };

      const onMouseMove = (moveEvent: MouseEvent) => {
        if (!propertiesResizeRef.current) return;
        const delta = propertiesResizeRef.current.startX - moveEvent.clientX;
        const next = Math.min(560, Math.max(260, propertiesResizeRef.current.startWidth + delta));
        setPropertiesPanelWidth(next);
      };
      const onMouseUp = () => {
        propertiesResizeRef.current = null;
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      };
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    },
    [propertiesPanelWidth],
  );

  const applyOrgLayout = useCallback(() => {
    const hierarchyEdges = edges.filter((edge) => isHierarchyEdge(edge, cardIds));
    const participantIds = new Set<string>();
    hierarchyEdges.forEach((edge) => {
      participantIds.add(edge.source);
      participantIds.add(edge.target);
    });

    if (participantIds.size === 0) {
      toast.info('Crie ligações diretas entre cards para usar o auto-layout');
      return;
    }

    const participants = nodes
      .filter((node) => participantIds.has(node.id))
      .map((node) => ({ ...node, ...nodeSizeOf(node) }));
    const { nodes: laidOut } = applyAutoLayout(participants, hierarchyEdges, {
      direction: 'TB',
      rankSep: 90,
      nodeSep: 48,
    });

    // Reposiciona o bloco organizado sobre o canto do arranjo original
    const originMinX = Math.min(...participants.map((node) => node.position.x));
    const originMinY = Math.min(...participants.map((node) => node.position.y));
    const layoutMinX = Math.min(...laidOut.map((node) => node.position.x));
    const layoutMinY = Math.min(...laidOut.map((node) => node.position.y));
    const offsetX = originMinX - layoutMinX;
    const offsetY = originMinY - layoutMinY;

    const positionById = new Map(
      laidOut.map((node) => [
        node.id,
        {
          x: Math.round((node.position.x + offsetX) / 16) * 16,
          y: Math.round((node.position.y + offsetY) / 16) * 16,
        },
      ]),
    );

    commitHistory();
    setNodes((current) =>
      current.map((node) => {
        const nextPosition = positionById.get(node.id);
        return nextPosition ? { ...node, position: nextPosition } : node;
      }),
    );
    setTimeout(() => fitView({ duration: 300, padding: 0.16 }), 60);
    toast.success('Organograma organizado — use Desfazer se preferir o arranjo anterior');
  }, [cardIds, commitHistory, edges, fitView, nodes, setNodes]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const isMod = event.metaKey || event.ctrlKey;
      if (isMod && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        searchInputRef.current?.focus();
        return;
      }
      if (event.key === '/' && !isEditableTarget(event.target)) {
        event.preventDefault();
        searchInputRef.current?.focus();
        return;
      }
      if (isMod && event.key.toLowerCase() === 'z' && !isEditableTarget(event.target)) {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
      }
      if (isMod && event.key.toLowerCase() === 'a' && !isEditableTarget(event.target)) {
        event.preventDefault();
        setNodes((current) => current.map((node) => ({ ...node, selected: true })));
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [redo, setNodes, undo]);

  useEffect(() => () => {
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
  }, []);

  const addCard = useCallback(
    (kind: OrgMvpNodeKind) => {
      if (!canEditCanvas) return;
      commitHistory();
      const id = newId(kind);
      const offset = nodes.filter((node) => node.type === 'card').length * 18;
      const { width, height } = lastCardSize;
      setNodes((current) => [
        ...deselectAll(current),
        {
          id,
          type: 'card',
          position: { x: 180 + offset, y: 220 + offset },
          width,
          height,
          zIndex: 10,
          selected: true,
          data: {
            kind,
            label: kind === 'person' ? 'Nova pessoa' : 'Novo departamento',
            subtitle: kind === 'person' ? 'Cargo ou função' : 'Área ou time',
            description: '',
            sourceType: kind === 'person' ? 'ORG_ONLY' : undefined,
            usuarioId: null,
            departamentoId: null,
            color: kind === 'person' ? '#2563EB' : '#059669',
            textColor: '#0F172A',
            figure: { type: 'catalog', key: kind === 'person' ? 'person' : 'building' },
          },
          style: { width, height },
        },
      ]);
      setSelectedNodeId(id);
      const posX = 180 + offset;
      const posY = 220 + offset;
      setTimeout(() => setCenter(posX + width / 2, posY + height / 2, { duration: 300, zoom: getZoom() }), 60);
    },
    [canEditCanvas, commitHistory, getZoom, lastCardSize, nodes, setCenter, setNodes],
  );

  const applyTemplate = useCallback(
    (template: OrganogramaTemplate) => {
      if (!canEditCanvas) return;
      const instantiated = instantiateTemplate(template);
      commitHistory();
      setNodes(instantiated.nodes);
      setEdges(instantiated.edges);
      setTemplateChooserOpen(false);
      setTimeout(() => fitView({ duration: 300, padding: 0.16 }), 60);
      toast.success(`Modelo "${template.nome}" aplicado — edite à vontade`);
    },
    [canEditCanvas, commitHistory, fitView, setEdges, setNodes],
  );

  // A chave do modelo já foi capturada em `requestedTemplateKeyRef` na montagem; sai da URL
  // para um F5 não reaplicar o modelo por cima do que o usuário já editou.
  useEffect(() => {
    if (!searchParams.has('template')) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('template');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleConfirmImport = useCallback(
    (result: ImportParseResult) => {
      if (!canEditCanvas) return;
      const { nodes: builtNodes, edges: builtEdges } = buildNodesAndEdgesFromGrupos(result.grupos);
      const positionedNodes = offsetImportedNodes(builtNodes, nodes);
      commitHistory();
      setNodes((current) => [...current, ...positionedNodes]);
      setEdges((current) => [...current, ...builtEdges]);
      toast.success(`Importado: ${result.totalPessoas} pessoa(s) em ${result.grupos.length} grupo(s)`);
      setTimeout(() => fitView({ duration: 300, padding: 0.16 }), 60);
    },
    [canEditCanvas, commitHistory, fitView, nodes, setEdges, setNodes],
  );

  const addFigure = useCallback((figureKey: OrgMvpFigureKey = 'forklift') => {
    if (!canEditCanvas) return;
    commitHistory();
    const id = newId('figure');
    const offset = nodes.filter((node) => node.type === 'figure').length * 20;
    const posX = 260 + offset;
    const posY = 320 + offset;
    const catalogItem = figureCatalogByKey.get(figureKey);
    setNodes((current) => [
      ...deselectAll(current),
      {
        id,
        type: 'figure',
        position: { x: posX, y: posY },
        width: 96,
        height: 96,
        zIndex: 20,
        selected: true,
        data: {
          figure: { type: 'catalog', key: figureKey },
          color: '#EA580C',
          label: catalogItem?.label || 'Figura',
          showBackground: false,
        },
        style: { width: 96, height: 96 },
      },
    ]);
    setSelectedNodeId(id);
    setTimeout(() => setCenter(posX + 48, posY + 48, { duration: 300, zoom: getZoom() }), 60);
  }, [canEditCanvas, commitHistory, getZoom, nodes, setCenter, setNodes]);

  const addText = useCallback(() => {
    if (!canEditCanvas) return;
    commitHistory();
    const id = newId('text');
    const offset = nodes.filter((node) => node.type === 'text').length * 16;
    const posX = 240 + offset;
    const posY = 180 + offset;
    setNodes((current) => [
      ...deselectAll(current),
      {
        id,
        type: 'text',
        position: { x: posX, y: posY },
        width: 200,
        height: 56,
        zIndex: 30,
        selected: true,
        data: { text: '', color: '#0F172A', fontSize: 16 },
        style: { width: 200, height: 56 },
      },
    ]);
    setSelectedNodeId(id);
    setTimeout(() => setCenter(posX + 100, posY + 28, { duration: 300, zoom: getZoom() }), 60);
  }, [canEditCanvas, commitHistory, getZoom, nodes, setCenter, setNodes]);

  const addAnnotation = useCallback(() => {
    if (!canEditCanvas) return;
    commitHistory();
    const id = newId('annotation');
    const offset = nodes.filter((node) => node.type === 'annotation').length * 16;
    const posX = 300 + offset;
    const posY = 240 + offset;
    setNodes((current) => [
      ...deselectAll(current),
      {
        id,
        type: 'annotation',
        position: { x: posX, y: posY },
        width: 180,
        height: 140,
        zIndex: 30,
        selected: true,
        data: { text: '', color: '#FEF08A' },
        style: { width: 180, height: 140 },
      },
    ]);
    setSelectedNodeId(id);
    setTimeout(() => setCenter(posX + 90, posY + 70, { duration: 300, zoom: getZoom() }), 60);
  }, [canEditCanvas, commitHistory, getZoom, nodes, setCenter, setNodes]);

  const addColumn = useCallback(() => {
    if (!canEditCanvas) return;
    commitHistory();
    const id = newId('column');
    const posX = 140 + nodes.length * 12;
    const posY = 60 + nodes.length * 8;
    setNodes((current) => [
      ...deselectAll(current),
      {
        id,
        type: 'column',
        position: { x: posX, y: posY },
        width: 340,
        height: 620,
        zIndex: 0,
        selected: true,
        data: {
          title: 'Nova coluna',
          color: '#E0F2FE',
          orientation: 'vertical',
        },
        style: { width: 340, height: 620 },
      },
    ]);
    setSelectedNodeId(id);
    setTimeout(() => setCenter(posX + 170, posY + 310, { duration: 300, zoom: getZoom() }), 60);
  }, [canEditCanvas, commitHistory, getZoom, nodes, setCenter, setNodes]);

  const addShape = useCallback(
    (shape: OrgMvpShapeKind) => {
      if (!canEditCanvas) return;
      commitHistory();
      const id = newId('shape');
      const { width, height } = SHAPE_OPTIONS.find((option) => option.shape === shape)?.size || { width: 160, height: 160 };
      const offset = nodes.filter((node) => node.type === 'shape').length * 18;
      const posX = 220 + offset;
      const posY = 200 + offset;
      setNodes((current) => [
        ...deselectAll(current),
        {
          id,
          type: 'shape',
          position: { x: posX, y: posY },
          width,
          height,
          zIndex: 5,
          selected: true,
          data: { shape, dashed: false, color: '#334155', fill: 'transparent', strokeWidth: 2 },
          style: { width, height },
        },
      ]);
      setSelectedNodeId(id);
      setTimeout(() => setCenter(posX + width / 2, posY + height / 2, { duration: 300, zoom: getZoom() }), 60);
    },
    [canEditCanvas, commitHistory, getZoom, nodes, setCenter, setNodes],
  );

  /** Stepper de tamanho da barra de ferramentas. `scale` mantém a diferença relativa entre
   *  os itens selecionados; `absoluteWidth` iguala a largura de todos (altura acompanha a proporção). */
  const resizeSelectedNodes = useCallback(
    (change: { scale: number } | { absoluteWidth: number }) => {
      if (!canEditCanvas) return;
      const targetIds = new Set(
        nodesRef.current.filter((node) => node.selected).map((node) => node.id),
      );
      if (targetIds.size === 0 && selectedNodeId) targetIds.add(selectedNodeId);
      if (targetIds.size === 0) return;

      commitHistory();
      setNodes((current) =>
        current.map((node) => {
          if (!targetIds.has(node.id)) return node;
          const size = nodeSizeOf(node);
          const factor = 'scale' in change ? change.scale : change.absoluteWidth / size.width;
          const width = clampNodeSize(size.width * factor);
          const height = clampNodeSize(size.height * factor);
          if (node.type === 'card') setLastCardSize({ width, height });
          return { ...node, width, height, style: { ...node.style, width, height } };
        }),
      );
    },
    [canEditCanvas, commitHistory, selectedNodeId, setNodes],
  );

  const deleteSelected = useCallback(() => {
    if (!canEditCanvas) return;

    const nodeIdsToDelete = new Set(nodesRef.current.filter((node) => node.selected).map((node) => node.id));
    if (selectedNodeId) nodeIdsToDelete.add(selectedNodeId);
    const edgeIdsToDelete = new Set(edgesRef.current.filter((edge) => edge.selected).map((edge) => edge.id));
    if (selectedEdgeId) edgeIdsToDelete.add(selectedEdgeId);
    if (nodeIdsToDelete.size === 0 && edgeIdsToDelete.size === 0) return;

    commitHistory();
    if (nodeIdsToDelete.size > 0) {
      setNodes((current) => current.filter((node) => !nodeIdsToDelete.has(node.id)));
      setSelectedNodeId(null);
    }
    setEdges((current) =>
      current.filter(
        (edge) =>
          !edgeIdsToDelete.has(edge.id)
          && !nodeIdsToDelete.has(edge.source)
          && !nodeIdsToDelete.has(edge.target),
      ),
    );
    setSelectedEdgeId(null);
  }, [canEditCanvas, commitHistory, selectedEdgeId, selectedNodeId, setEdges, setNodes]);

  const duplicateSelected = useCallback(() => {
    if (!canEditCanvas || !selectedNode) return;
    commitHistory();
    const id = newId(selectedNode.type || 'node');
    const clone = {
      ...selectedNode,
      id,
      position: { x: selectedNode.position.x + DUPLICATE_OFFSET, y: selectedNode.position.y + DUPLICATE_OFFSET },
      selected: true,
      data: { ...selectedNode.data },
    } as OrgMvpNode;
    setNodes((current) => [...current.map((n) => ({ ...n, selected: false })), clone]);
    setSelectedNodeId(id);
  }, [canEditCanvas, commitHistory, selectedNode, setNodes]);

  const copySelected = useCallback(() => {
    if (!selectedNode || !organogramaId) return;
    try {
      localStorage.setItem(getClipboardKey(organogramaId), JSON.stringify(selectedNode));
    } catch {
      // ignora — quota excedida ou storage indisponível
    }
  }, [organogramaId, selectedNode]);

  const pasteClipboard = useCallback(() => {
    if (!canEditCanvas || !organogramaId) return;
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(getClipboardKey(organogramaId));
    } catch {
      raw = null;
    }
    if (!raw) return;
    let copied: OrgMvpNode | null = null;
    try {
      copied = JSON.parse(raw);
    } catch {
      return;
    }
    if (!copied || typeof copied.position?.x !== 'number' || typeof copied.position?.y !== 'number') return;
    commitHistory();
    const id = newId(copied.type || 'node');
    const clone = {
      ...copied,
      id,
      position: { x: copied.position.x + PASTE_OFFSET, y: copied.position.y + PASTE_OFFSET },
      selected: true,
    } as OrgMvpNode;
    setNodes((current) => [...current.map((n) => ({ ...n, selected: false })), clone]);
    setSelectedNodeId(id);
  }, [canEditCanvas, commitHistory, organogramaId, setNodes]);

  const openNodeContextMenu = useCallback(
    (event: React.MouseEvent, node: OrgMvpNode) => {
      event.preventDefault();
      if (!canEditCanvas) return;
      setSelectedNodeId(node.id);
      setSelectedEdgeId(null);
      setCtxMenu({ type: 'node', id: node.id, x: event.clientX, y: event.clientY });
    },
    [canEditCanvas],
  );

  const openEdgeContextMenu = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      event.preventDefault();
      if (!canEditCanvas) return;
      setSelectedEdgeId(edge.id);
      setSelectedNodeId(null);
      setCtxMenu({ type: 'edge', id: edge.id, x: event.clientX, y: event.clientY });
    },
    [canEditCanvas],
  );

  const openPaneContextMenu = useCallback(
    (event: React.MouseEvent | MouseEvent) => {
      event.preventDefault();
      if (!canEditCanvas) return;
      setCtxMenu({ type: 'pane', id: null, x: event.clientX, y: event.clientY });
    },
    [canEditCanvas],
  );

  const buildContextItems = useCallback(
    (ctx: CtxTarget): Array<CtxItem | 'separator'> => {
      if (ctx.type === 'node') {
        return [
          { label: 'Duplicar', shortcut: '⌘D', onClick: duplicateSelected },
          { label: 'Copiar', shortcut: '⌘C', onClick: copySelected },
          { label: 'Colar', shortcut: '⌘V', onClick: pasteClipboard },
          'separator',
          { label: 'Excluir', shortcut: 'Del', onClick: deleteSelected, danger: true },
        ];
      }
      if (ctx.type === 'edge') {
        return [
          {
            label: 'Excluir',
            shortcut: 'Del',
            onClick: () => ctx.id && setPendingEdgeDeleteId(ctx.id),
            danger: true,
          },
        ];
      }
      return [
        { label: 'Colar', shortcut: '⌘V', onClick: pasteClipboard },
        'separator',
        { label: 'Organizar automaticamente', onClick: () => setIsLayoutConfirmOpen(true) },
        { label: 'Enquadrar visualização', onClick: () => fitView({ duration: 250, padding: 0.16 }) },
      ];
    },
    [copySelected, deleteSelected, duplicateSelected, fitView, pasteClipboard],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const isMod = event.metaKey || event.ctrlKey;
      if (isMod && event.key.toLowerCase() === 'd' && !isEditableTarget(event.target)) {
        event.preventDefault();
        duplicateSelected();
        return;
      }
      if (isMod && event.key.toLowerCase() === 'c' && !isEditableTarget(event.target)) {
        event.preventDefault();
        copySelected();
        return;
      }
      if (isMod && event.key.toLowerCase() === 'v' && !isEditableTarget(event.target)) {
        event.preventDefault();
        pasteClipboard();
        return;
      }
      // Delete/Backspace nunca pode disparar durante edição de texto (card, anotação,
      // título de coluna, campo do painel) — lá a tecla apaga um caractere.
      if ((event.key === 'Delete' || event.key === 'Backspace') && !isEditableTarget(event.target)) {
        event.preventDefault();
        deleteSelected();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [copySelected, deleteSelected, duplicateSelected, pasteClipboard]);

  const isValidConnection = useCallback(
    (connection: Connection | Edge) => {
      if (!connection.source || !connection.target) return false;
      if (connection.source === connection.target) return false;
      const ignoreEdgeId = 'id' in connection && typeof connection.id === 'string' ? connection.id : undefined;
      return !wouldCreateCycle(connection.source, connection.target, edges, cardIds, ignoreEdgeId);
    },
    [cardIds, edges],
  );

  const handleConnect = useCallback(
    (connection: Connection) => {
      if (!canEditCanvas) return;
      if (!connection.source || !connection.target || connection.source === connection.target) return;
      if (wouldCreateCycle(connection.source, connection.target, edges, cardIds)) {
        toast.error('Conexão bloqueada: criaria um ciclo na hierarquia');
        return;
      }
      justConnectedRef.current = true;
      commitHistory();
      const source = nodes.find((node) => node.id === connection.source);
      const sourceColor = source?.type === 'card' ? (source.data as OrgMvpCardData).color : '#334155';
      setEdges((current) =>
        addEdge(
          {
            ...connection,
            id: newId('edge'),
            type: 'smoothstep',
            style: { stroke: sourceColor, strokeWidth: 2.2 },
          },
          current,
        ),
      );
    },
    [canEditCanvas, cardIds, commitHistory, edges, nodes, setEdges],
  );

  const handleEdgeDoubleClick = useCallback<EdgeMouseHandler>(
    (_event, edge) => {
      setPendingEdgeDeleteId(edge.id);
    },
    [],
  );

  const confirmEdgeDelete = useCallback(() => {
    if (!canEditCanvas) return;
    if (!pendingEdgeDeleteId) return;
    commitHistory();
    setEdges((current) => current.filter((item) => item.id !== pendingEdgeDeleteId));
    setPendingEdgeDeleteId(null);
  }, [canEditCanvas, commitHistory, pendingEdgeDeleteId, setEdges]);

  const handleEdgeUpdate = useCallback(
    (updates: { stroke?: string; strokeWidth?: number; tipo?: OrgMvpEdgeTipo; edgeType?: 'smoothstep' | 'straight' | 'default' }) => {
      if (!canEditCanvas || !selectedEdgeId) return;
      commitHistory();
      setEdges((current) =>
        current.map((edge) => {
          if (edge.id !== selectedEdgeId) return edge;
          const next: Edge = { ...edge };
          if (updates.edgeType) next.type = updates.edgeType;
          if (updates.tipo) next.data = { ...(edge.data || {}), tipo: updates.tipo };
          if (updates.stroke !== undefined || updates.strokeWidth !== undefined) {
            next.style = {
              ...(edge.style || {}),
              stroke: updates.stroke ?? (typeof edge.style?.stroke === 'string' ? edge.style.stroke : '#334155'),
              strokeWidth: updates.strokeWidth ?? (typeof edge.style?.strokeWidth === 'number' ? edge.style.strokeWidth : 2.2),
            };
          }
          return next;
        }),
      );
    },
    [canEditCanvas, commitHistory, selectedEdgeId, setEdges],
  );

  const isReconnectingRef = useRef(false);

  const handleReconnectStart = useCallback(() => {
    isReconnectingRef.current = true;
  }, []);

  const handleReconnect = useCallback(
    (oldEdge: Edge, newConnection: Connection) => {
      if (!canEditCanvas) return;
      if (!newConnection.source || !newConnection.target || newConnection.source === newConnection.target) return;
      if (
        getEdgeTipo(oldEdge) === 'direta'
        && wouldCreateCycle(newConnection.source, newConnection.target, edges, cardIds, oldEdge.id)
      ) {
        toast.error('Reconexão bloqueada: criaria um ciclo na hierarquia');
        return;
      }
      commitHistory();
      setEdges((current) => reconnectEdge(oldEdge, newConnection, current, { shouldReplaceId: false }));
    },
    [canEditCanvas, cardIds, commitHistory, edges, setEdges],
  );

  const handleReconnectEnd = useCallback(() => {
    setTimeout(() => {
      isReconnectingRef.current = false;
    }, 0);
  }, []);

  const performSave = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!organogramaId || !canEditCanvas) return;
    if (saveBlockedByConflict) return;
    if (savingRef.current) return;
    savingRef.current = true;
    setIsSavingCanvas(true);

    const payloadNodes = nodesRef.current;
    const payloadEdges = edgesRef.current;
    const payload = {
      nodes: mapFlowNodesToApi(payloadNodes),
      edges: mapFlowEdgesToApi(payloadEdges),
      updatedAt: organogramaUpdatedAt,
    };
    const result = await organogramasService.saveCanvas(organogramaId, payload);
    savingRef.current = false;
    setIsSavingCanvas(false);

    if (result.success) {
      autoSaveFailuresRef.current = 0;
      const nodeMapping = result.data?.idMapping || {};
      const edgeMapping = result.data?.edgeIdMapping || {};
      const hasMapping = Object.keys(nodeMapping).length > 0 || Object.keys(edgeMapping).length > 0;

      const savedState = hasMapping
        ? rebaseIds(payloadNodes, payloadEdges, nodeMapping, edgeMapping)
        : { nodes: payloadNodes, edges: payloadEdges };
      baselineSnapshotRef.current = serializeCanvasSnapshot(savedState.nodes, savedState.edges);

      if (hasMapping) {
        const mapNodeId = (id: string) => nodeMapping[id] || id;
        setNodes((current) => rebaseIds(current, [], nodeMapping, edgeMapping).nodes);
        setEdges((current) => rebaseIds([], current, nodeMapping, edgeMapping).edges);
        setSelectedNodeId((current) => (current ? mapNodeId(current) : current));
        setSelectedEdgeId((current) => (current ? edgeMapping[current] || current : current));
        setCollapsedIds((current) => new Set([...current].map(mapNodeId)));
        const rebaseSnapshot = (snapshot: OrgMvpHistorySnapshot): OrgMvpHistorySnapshot => {
          const rebased = rebaseIds(snapshot.nodes, snapshot.edges, nodeMapping, edgeMapping);
          return {
            nodes: rebased.nodes,
            edges: rebased.edges,
            selectedNodeId: snapshot.selectedNodeId ? mapNodeId(snapshot.selectedNodeId) : null,
          };
        };
        setUndoStack((current) => current.map(rebaseSnapshot));
        setRedoStack((current) => current.map(rebaseSnapshot));
      }

      // Pode continuar sujo se o usuário editou durante o save em voo
      const currentState = hasMapping
        ? rebaseIds(nodesRef.current, edgesRef.current, nodeMapping, edgeMapping)
        : { nodes: nodesRef.current, edges: edgesRef.current };
      setDirty(serializeCanvasSnapshot(currentState.nodes, currentState.edges) !== baselineSnapshotRef.current);

      setLastSavedAt(new Date());
      setOrganogramaUpdatedAt(result.data?.updatedAt || organogramaUpdatedAt);
      if (!silent) toast.success('Organograma salvo com sucesso');
      return;
    }

    if (result.error?.includes('desatualizado')) {
      setSaveBlockedByConflict(true);
      setSaveConflictOpen(true);
      if (silent) {
        setAutoSaveDisabled(true);
      }
      return;
    }

    if (silent) {
      autoSaveFailuresRef.current += 1;
      if (autoSaveFailuresRef.current >= AUTO_SAVE_MAX_FAILURES) {
        setAutoSaveDisabled(true);
        toast.error(`Auto-save desativado após falhas seguidas: ${result.error || 'erro desconhecido'}. Salve manualmente.`, { duration: 10000 });
      }
      return;
    }

    toast.error(result.error || 'Erro ao salvar canvas');
  }, [canEditCanvas, organogramaId, organogramaUpdatedAt, saveBlockedByConflict, setEdges, setNodes]);

  const saveCanvas = useCallback(() => {
    void performSave({ silent: false });
  }, [performSave]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const isMod = event.metaKey || event.ctrlKey;
      if (isMod && event.key.toLowerCase() === 's') {
        event.preventDefault();
        if (saveBlockedByConflict) {
          setSaveConflictOpen(true);
        } else if (canEditCanvas && !isSavingCanvas) {
          saveCanvas();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [canEditCanvas, isSavingCanvas, saveBlockedByConflict, saveCanvas]);

  useEffect(() => {
    if (!canEditCanvas || autoSaveDisabled || saveBlockedByConflict || isLoadingCanvas || isSavingCanvas || isDraggingNode || !dirty) return;
    const timer = setTimeout(() => {
      void performSave({ silent: true });
    }, AUTO_SAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [autoSaveDisabled, canEditCanvas, dirty, edges, isDraggingNode, isLoadingCanvas, isSavingCanvas, nodes, performSave, saveBlockedByConflict]);

  const handleConnectStart = useCallback((_event: unknown, params: { nodeId: string; handleId: string | null; handleType: 'source' | 'target' | null }) => {
    pendingConnectSourceRef.current = { sourceId: params.nodeId, sourceHandle: params.handleId, handleType: params.handleType };
  }, []);

  const handleConnectEnd = useCallback((event: MouseEvent | TouchEvent, connectionState?: { isValid: boolean }) => {
    if (isReconnectingRef.current) {
      justConnectedRef.current = false;
      pendingConnectSourceRef.current = null;
      return;
    }

    if (justConnectedRef.current || connectionState?.isValid) {
      justConnectedRef.current = false;
      pendingConnectSourceRef.current = null;
      return;
    }

    const source = pendingConnectSourceRef.current;
    pendingConnectSourceRef.current = null;
    if (!source) return;

    const point = 'changedTouches' in event
      ? { x: event.changedTouches[0]?.clientX ?? 0, y: event.changedTouches[0]?.clientY ?? 0 }
      : { x: event.clientX, y: event.clientY };
    const flowPoint = screenToFlowPosition({ x: point.x, y: point.y });

    // Se soltou sobre um componente existente, conecta direto sem abrir modal
    const sourceNode = nodes.find((node) => node.id === source.sourceId);
    const hoveredNode = nodes.find((node) => {
      if (node.id === source.sourceId) return false;
      const width = typeof node.width === 'number' ? node.width : Number(node.style?.width) || 0;
      const height = typeof node.height === 'number' ? node.height : Number(node.style?.height) || 0;
      return (
        flowPoint.x >= node.position.x &&
        flowPoint.x <= node.position.x + width &&
        flowPoint.y >= node.position.y &&
        flowPoint.y <= node.position.y + height
      );
    });

    if (hoveredNode && canEditCanvas) {
      const centerX = hoveredNode.position.x + (typeof hoveredNode.width === 'number' ? hoveredNode.width : Number(hoveredNode.style?.width) || 0) / 2;
      const centerY = hoveredNode.position.y + (typeof hoveredNode.height === 'number' ? hoveredNode.height : Number(hoveredNode.style?.height) || 0) / 2;
      const dx = flowPoint.x - centerX;
      const dy = flowPoint.y - centerY;
      const normalizedSourceHandle = (source.sourceHandle ?? 'right').replace(/^t-/, '') as 'top' | 'right' | 'bottom' | 'left';
      const targetHandle = Math.abs(dx) > Math.abs(dy)
        ? (dx < 0 ? 't-left' : 't-right')
        : (dy < 0 ? 't-top' : 't-bottom');
      const sourceColor = sourceNode?.type === 'card' ? (sourceNode.data as OrgMvpCardData).color : '#334155';
      const startedFromTarget = source.handleType === 'target';
      const edgeSource = startedFromTarget ? hoveredNode.id : source.sourceId;
      const edgeTarget = startedFromTarget ? source.sourceId : hoveredNode.id;

      if (wouldCreateCycle(edgeSource, edgeTarget, edges, cardIds)) {
        toast.error('Conexão bloqueada: criaria um ciclo na hierarquia');
        return;
      }

      commitHistory();
      setEdges((current) =>
        addEdge(
          {
            id: newId('edge'),
            source: startedFromTarget ? hoveredNode.id : source.sourceId,
            target: startedFromTarget ? source.sourceId : hoveredNode.id,
            sourceHandle: startedFromTarget
              ? oppositeSourceHandleBySource[normalizedSourceHandle] ?? 'right'
              : normalizedSourceHandle,
            targetHandle: startedFromTarget ? source.sourceHandle ?? undefined : targetHandle,
            type: 'smoothstep',
            style: { stroke: sourceColor, strokeWidth: 2.2 },
          },
          current,
        ),
      );
      justConnectedRef.current = true;
      return;
    }

    const targetElement = event.target as Element | null;
    const droppedOnPane =
      !!targetElement &&
      (targetElement.classList.contains('react-flow__pane') || !!targetElement.closest('.react-flow__pane'));
    if (!droppedOnPane) return;

    setPendingConnectionDraft({
      sourceId: source.sourceId,
      sourceHandle: source.sourceHandle,
      handleType: source.handleType,
      clientX: point.x,
      clientY: point.y,
    });
  }, [canEditCanvas, cardIds, commitHistory, edges, nodes, screenToFlowPosition, setEdges]);

  const createConnectedNodeFromDraft = useCallback(
    (kind: 'figure' | OrgMvpNodeKind) => {
      const draft = pendingConnectionDraft;
      if (!draft) return;

      commitHistory();
      const id = newId(kind);
      const sourceNode = nodes.find((node) => node.id === draft.sourceId);
      const basePosition = screenToFlowPosition({ x: draft.clientX, y: draft.clientY });
      const nextWidth = kind === 'figure' ? 96 : 220;
      const nextHeight = kind === 'figure' ? 96 : 118;
      const normalizedSourceHandle = (draft.sourceHandle ?? 'right').replace(/^t-/, '') as 'top' | 'right' | 'bottom' | 'left';

      let position = basePosition;
      if (sourceNode) {
        const sourceWidth = typeof sourceNode.width === 'number' ? sourceNode.width : Number(sourceNode.style?.width) || 0;
        const sourceHeight = typeof sourceNode.height === 'number' ? sourceNode.height : Number(sourceNode.style?.height) || 0;
        const sourceCenterX = sourceNode.position.x + sourceWidth / 2;
        const sourceCenterY = sourceNode.position.y + sourceHeight / 2;

        if (normalizedSourceHandle === 'top' || normalizedSourceHandle === 'bottom') {
          position = { x: sourceCenterX - nextWidth / 2, y: basePosition.y };
        } else {
          position = { x: basePosition.x, y: sourceCenterY - nextHeight / 2 };
        }
      }

      if (kind === 'figure') {
        setNodes((current) => [
          ...current,
          {
            id,
            type: 'figure',
            position,
            width: 96,
            height: 96,
            zIndex: 20,
            data: {
              figure: { type: 'catalog', key: 'forklift' },
              color: '#EA580C',
              label: 'Figura',
              showBackground: false,
            },
            style: { width: 96, height: 96 },
          },
        ]);
      } else {
        setNodes((current) => [
          ...current,
          {
            id,
            type: 'card',
            position,
            width: 220,
            height: 118,
            zIndex: 10,
            data: {
              kind,
              label: kind === 'person' ? 'Nova pessoa' : 'Novo departamento',
              subtitle: kind === 'person' ? 'Cargo ou função' : 'Área ou time',
              description: '',
              color: kind === 'person' ? '#2563EB' : '#059669',
              textColor: '#0F172A',
              figure: { type: 'catalog', key: kind === 'person' ? 'person' : 'building' },
            },
            style: { width: 220, height: 118 },
          },
        ]);
      }

      const sourceColor = (sourceNode?.data as { color?: string } | undefined)?.color ?? '#334155';
      const startedFromTarget = draft.handleType === 'target';
      const edgeSourceId = startedFromTarget ? id : draft.sourceId;
      const edgeTargetId = startedFromTarget ? draft.sourceId : id;
      const edgeSourceHandle = startedFromTarget
        ? oppositeSourceHandleBySource[normalizedSourceHandle] ?? 'right'
        : normalizedSourceHandle;
      const edgeTargetHandle = startedFromTarget
        ? draft.sourceHandle
        : oppositeHandleBySource[normalizedSourceHandle] ?? 't-left';

      setTimeout(() => {
        setEdges((current) =>
          addEdge(
            {
              id: newId('edge'),
              source: edgeSourceId,
              target: edgeTargetId,
              sourceHandle: edgeSourceHandle,
              targetHandle: edgeTargetHandle ?? undefined,
              type: 'straight',
              style: { stroke: sourceColor, strokeWidth: 2.2 },
            },
            current,
          ),
        );
      }, 0);
      setSelectedNodeId(id);
      setPendingConnectionDraft(null);
    },
    [commitHistory, nodes, pendingConnectionDraft, screenToFlowPosition, setEdges, setNodes],
  );

  const handlePaneDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const nodeKind = event.dataTransfer.getData('application/x-org-mvp-kind') as OrgMvpNodeKind | 'column' | 'figure' | 'text' | 'table' | 'annotation' | 'shape';
      if (!nodeKind) return;

      commitHistory();
      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      if (nodeKind === 'column') {
        const id = newId('column');
        setNodes((current) => [
          ...current,
          {
            id,
            type: 'column',
            position,
            width: 340,
            height: 620,
            zIndex: 0,
            data: { title: 'Nova coluna', color: '#E0F2FE', orientation: 'vertical' },
            style: { width: 340, height: 620 },
          },
        ]);
        return;
      }

      if (nodeKind === 'figure') {
        const id = newId('figure');
        setNodes((current) => [
          ...current,
          {
            id,
            type: 'figure',
            position,
            width: 96,
            height: 96,
            zIndex: 20,
            data: {
              figure: { type: 'catalog', key: 'forklift' },
              color: '#EA580C',
              label: 'Figura',
              showBackground: false,
            },
            style: { width: 96, height: 96 },
          },
        ]);
        return;
      }

      if (nodeKind === 'text') {
        const id = newId('text');
        setNodes((current) => [
          ...current,
          { id, type: 'text', position, width: 200, height: 56, zIndex: 30, data: { text: '', color: '#0F172A', fontSize: 16 }, style: { width: 200, height: 56 } },
        ]);
        return;
      }

      if (nodeKind === 'annotation') {
        const id = newId('annotation');
        setNodes((current) => [
          ...current,
          { id, type: 'annotation', position, width: 180, height: 140, zIndex: 30, data: { text: '', color: '#FEF08A' }, style: { width: 180, height: 140 } },
        ]);
        return;
      }

      if (nodeKind === 'shape') {
        const id = newId('shape');
        setNodes((current) => [
          ...current,
          {
            id,
            type: 'shape',
            position,
            width: 160,
            height: 160,
            zIndex: 5,
            data: { shape: 'square', dashed: false, color: '#334155', fill: 'transparent', strokeWidth: 2 },
            style: { width: 160, height: 160 },
          },
        ]);
        return;
      }

      const id = newId(nodeKind);
      setNodes((current) => [
        ...current,
        {
          id,
          type: 'card',
          position,
          width: 220,
          height: 118,
          zIndex: 10,
          data: {
            kind: nodeKind,
            label: nodeKind === 'person' ? 'Nova pessoa' : 'Novo departamento',
            subtitle: nodeKind === 'person' ? 'Cargo ou função' : 'Área ou time',
            description: '',
            color: nodeKind === 'person' ? '#2563EB' : '#059669',
            textColor: '#0F172A',
            figure: { type: 'catalog', key: nodeKind === 'person' ? 'person' : 'building' },
          },
          style: { width: 220, height: 118 },
        },
      ]);
    },
    [commitHistory, screenToFlowPosition, setNodes],
  );

  const handleNodeDragStart = useCallback(() => {
    setIsDraggingNode(true);
    dragStartSnapshotRef.current = makeHistorySnapshot(nodes, edges, selectedNodeId);
  }, [edges, nodes, selectedNodeId]);

  const handleNodeDragStop = useCallback(() => {
    setIsDraggingNode(false);
    setHelperLineH(undefined);
    setHelperLineV(undefined);
    const dragStartSnapshot = dragStartSnapshotRef.current;
    if (!dragStartSnapshot) return;

    dragStartSnapshotRef.current = null;
    const currentSnapshot = makeHistorySnapshot(nodes, edges, selectedNodeId);
    if (!historySnapshotsEqual(dragStartSnapshot, currentSnapshot)) {
      pushUndoSnapshot(dragStartSnapshot);
    }
  }, [edges, nodes, pushUndoSnapshot, selectedNodeId]);

  const handleNodesChangeWithGuides = useCallback(
    (changes: NodeChange[]) => {
      let nextChanges = changes;

      // Resize por arraste do NodeResizer num card: memoriza o tamanho para o próximo card criado.
      changes.forEach((change) => {
        if (change.type !== 'dimensions' || !change.resizing || !change.dimensions) return;
        const node = nodesRef.current.find((item) => item.id === change.id);
        if (node?.type !== 'card') return;
        setLastCardSize({
          width: clampNodeSize(change.dimensions.width),
          height: clampNodeSize(change.dimensions.height),
        });
      });

      // Não filtra por `dragging`: o último evento de um gesto de arraste (ao soltar
      // o mouse) chega com `dragging: false` e posição recalculada do zero pelo
      // React Flow — sem aplicar o snap aqui também, o node "descola" do alinhamento
      // visual mostrado durante o arraste assim que o usuário solta o botão.
      const positionChanges = changes.filter(
        (c): c is NodePositionChange => c.type === 'position',
      );
      if (positionChanges.length === 1 && positionChanges[0].position) {
        const helpers = getHelperLines(positionChanges[0], nodes);
        if (positionChanges[0].dragging) {
          setHelperLineH(helpers.horizontal);
          setHelperLineV(helpers.vertical);
        } else {
          setHelperLineH(undefined);
          setHelperLineV(undefined);
        }
        if (helpers.snapPosition.x !== undefined || helpers.snapPosition.y !== undefined) {
          nextChanges = changes.map((c) => {
            if (c.type !== 'position' || c.id !== positionChanges[0].id || !c.position) return c;
            return {
              ...c,
              position: {
                x: helpers.snapPosition.x ?? c.position.x,
                y: helpers.snapPosition.y ?? c.position.y,
              },
            };
          });
        }
      } else if (helperLineH !== undefined || helperLineV !== undefined) {
        setHelperLineH(undefined);
        setHelperLineV(undefined);
      }
      onNodesChange(nextChanges);
    },
    [helperLineH, helperLineV, nodes, onNodesChange],
  );

  const snapColumnsTogether = useCallback(() => {
    const columns = nodes
      .filter((node) => node.type === 'column')
      .sort((a, b) => a.position.x - b.position.x || a.position.y - b.position.y);
    if (columns.length < 2) return;

    commitHistory();
    const startX = Math.min(...columns.map((node) => node.position.x));
    const startY = Math.min(...columns.map((node) => node.position.y));
    let nextX = startX;

    const nextPositionById = new Map<string, { x: number; y: number }>();
    columns.forEach((column) => {
      const width = typeof column.width === 'number' ? column.width : Number(column.style?.width) || 340;
      nextPositionById.set(column.id, { x: nextX, y: startY });
      nextX += width;
    });

    setNodes((current) =>
      current.map((node) => {
        const nextPosition = nextPositionById.get(node.id);
        return nextPosition ? { ...node, position: nextPosition } : node;
      }),
    );
    setTimeout(() => fitView({ duration: 250, padding: 0.14 }), 50);
  }, [commitHistory, fitView, nodes, setNodes]);

  const toggleFullscreen = useCallback(async () => {
    const element = workspaceRef.current;
    if (!element) return;

    if (document.fullscreenElement) {
      await document.exitFullscreen().catch(() => undefined);
      return;
    }

    await element.requestFullscreen?.().catch(() => undefined);
    setTimeout(() => fitView({ duration: 250, padding: 0.12 }), 200);
  }, [fitView]);

  const handleExportPng = useCallback(async () => {
    const element = exportAreaRef.current;
    if (!element) {
      toast.error('Área do canvas não encontrada para exportação');
      return;
    }

    try {
      await exportElementAsPng(element, {
        filename: `${slugify(organogramaNome || 'organograma')}.png`,
        backgroundColor: '#f8f4ea',
        pixelRatio: 2,
      });
      toast.success('PNG exportado com sucesso');
    } catch {
      toast.error('Erro ao exportar PNG');
    }
  }, [organogramaNome]);

  const handleExportPdf = useCallback(async () => {
    const element = exportAreaRef.current;
    if (!element) {
      toast.error('Área do canvas não encontrada para exportação');
      return;
    }

    try {
      await exportElementAsPdf(element, {
        filename: `${slugify(organogramaNome || 'organograma')}.pdf`,
        backgroundColor: '#f8f4ea',
        pixelRatio: 2,
      });
      toast.success('PDF exportado com sucesso');
    } catch {
      toast.error('Erro ao exportar PDF');
    }
  }, [organogramaNome]);

  const handleGenerateShareLink = useCallback(async () => {
    if (!currentOrganograma) return;
    const result = await organogramasService.generateShareLink(currentOrganograma.id);
    if (!result.success || !result.data?.share_token) {
      toast.error(result.error || 'Erro ao gerar link público');
      return;
    }

    const publicUrl = `${window.location.origin}/o/${result.data.share_token}`;
    await navigator.clipboard.writeText(publicUrl);
    setCurrentOrganograma((current) => current ? { ...current, share_token: result.data?.share_token || null } : current);
    toast.success('Link público copiado');
  }, [currentOrganograma]);

  const handleRevokeShareLink = useCallback(async () => {
    if (!currentOrganograma) return;
    const result = await organogramasService.revokeShareLink(currentOrganograma.id);
    if (!result.success) {
      toast.error(result.error || 'Erro ao revogar link público');
      return;
    }

    setCurrentOrganograma((current) => current ? { ...current, share_token: null } : current);
    toast.success('Link público revogado');
  }, [currentOrganograma]);

  const handleOpenHistorico = useCallback(async () => {
    if (!organogramaId) return;
    const result = await organogramasService.getHistorico(organogramaId);
    if (!result.success) {
      toast.error(result.error || 'Erro ao carregar histórico');
      return;
    }
    setHistorico(result.data || []);
    setIsHistoricoDialogOpen(true);
  }, [organogramaId]);

  const handleOpenPermissoes = useCallback(async () => {
    if (!organogramaId) return;
    const result = await organogramasService.getPermissoes(organogramaId);
    if (!result.success) {
      toast.error(result.error || 'Erro ao carregar permissões');
      return;
    }
    setPermissoes(result.data || []);
    setIsPermissoesDialogOpen(true);
  }, [organogramaId]);

  const handleChangePermissao = useCallback((usuarioId: string, role: 'none' | 'owner' | 'editor' | 'viewer') => {
    setPermissoes((current) => {
      const remaining = current.filter((item) => item.usuario_id !== usuarioId);
      if (role === 'none') return remaining;

      const usuario = usuarios.find((item) => item.id === usuarioId);
      return [
        ...remaining,
        {
          id: `${usuarioId}-${role}`,
          organograma_id: organogramaId || '',
          usuario_id: usuarioId,
          role,
          usuario: usuario ? { id: usuario.id, nome: usuario.nome, email: usuario.email } : undefined,
        },
      ];
    });
  }, [organogramaId, usuarios]);

  const handleSavePermissoes = useCallback(async () => {
    if (!organogramaId) return;
    const payload = permissoes.map((item) => ({ usuario_id: item.usuario_id, role: item.role }));
    const result = await organogramasService.updatePermissoes(organogramaId, payload);
    if (!result.success) {
      toast.error(result.error || 'Erro ao atualizar permissões');
      return;
    }
    setPermissoes(result.data || []);
    setIsPermissoesDialogOpen(false);
    toast.success('Permissões atualizadas');
  }, [organogramaId, permissoes]);

  const handleSaveMeta = useCallback(async () => {
    if (!currentOrganograma) return;
    setTentouSalvarMeta(true);
    if (!metaForm.nome.trim()) {
      toast.error('Informe o nome do organograma');
      return;
    }

    const result = await organogramasService.update(currentOrganograma.id, {
      nome: metaForm.nome.trim(),
      descricao: metaForm.descricao.trim() || null,
      tipo: metaForm.tipo,
      status: metaForm.status,
    });

    if (!result.success || !result.data) {
      toast.error(result.error || 'Erro ao atualizar metadados');
      return;
    }

    setCurrentOrganograma(result.data);
    setOrganogramaNome(result.data.nome);
    setIsMetaDialogOpen(false);
    toast.success('Metadados atualizados');
  }, [currentOrganograma, metaForm]);

  const commitRename = useCallback(async () => {
    setIsRenamingTitle(false);
    const trimmed = renameValue.trim();
    if (!currentOrganograma || !trimmed || trimmed === currentOrganograma.nome) return;

    const result = await organogramasService.update(currentOrganograma.id, {
      nome: trimmed,
      descricao: currentOrganograma.descricao || null,
      tipo: currentOrganograma.tipo,
      status: currentOrganograma.status,
    });

    if (!result.success || !result.data) {
      toast.error(result.error || 'Erro ao renomear organograma');
      return;
    }

    setCurrentOrganograma(result.data);
    setOrganogramaNome(result.data.nome);
    setMetaForm((current) => ({ ...current, nome: result.data.nome }));
    toast.success('Organograma renomeado');
  }, [currentOrganograma, renameValue]);

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border/70 bg-muted/20 px-2 py-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" asChild>
                <Link to="/organograma">
                  <ArrowLeft className="h-4 w-4" />
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Voltar para Organogramas</TooltipContent>
          </Tooltip>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/organograma">Organogramas</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                {isRenamingTitle ? (
                  <Input
                    autoFocus
                    value={renameValue}
                    onChange={(event) => setRenameValue(event.target.value)}
                    onBlur={commitRename}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        commitRename();
                      }
                      if (event.key === 'Escape') {
                        event.preventDefault();
                        setIsRenamingTitle(false);
                      }
                    }}
                    className="h-7 w-56 text-sm font-medium"
                  />
                ) : (
                  <BreadcrumbPage
                    className={cn(canEditCanvas && 'cursor-text rounded px-1 -mx-1 hover:bg-muted/60 hover:text-foreground')}
                    title={canEditCanvas ? 'Clique para renomear' : undefined}
                    onClick={() => {
                      if (!canEditCanvas) return;
                      setRenameValue(organogramaNome);
                      setIsRenamingTitle(true);
                    }}
                  >
                    {organogramaNome || 'Editor'}
                  </BreadcrumbPage>
                )}
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="mx-1 h-5 w-px bg-border" />

          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={undo} disabled={undoStack.length === 0}>
                <Undo2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Desfazer</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={redo} disabled={redoStack.length === 0}>
                <Redo2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Refazer</TooltipContent>
          </Tooltip>

          <div className="flex items-center rounded-md border border-border/70 bg-muted/20 p-0.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={interactionMode === 'select' ? 'secondary' : 'ghost'}
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setInteractionMode('select')}
                >
                  <MousePointer2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Selecionar (arraste para marcar itens)</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={interactionMode === 'pan' ? 'secondary' : 'ghost'}
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setInteractionMode('pan')}
                >
                  <Hand className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Mover (arraste para navegar pelo canvas)</TooltipContent>
            </Tooltip>
          </div>

          <SizeStepper
            width={selectedSizeWidth}
            count={sizeStepperCount}
            disabled={!canEditCanvas || sizeStepperCount === 0}
            onStep={(direction) =>
              resizeSelectedNodes({ scale: direction === 1 ? SIZE_STEP_FACTOR : 1 / SIZE_STEP_FACTOR })
            }
            onSetAbsolute={(value) => resizeSelectedNodes({ absoluteWidth: value })}
          />

          <div className="ml-auto flex items-center gap-2">
            <div className="flex items-center gap-1.5">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  ref={searchInputRef}
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      if (searchMatches.length === 0) return;
                      const current = searchMatches[searchIndex];
                      if (current && highlightedNodeId === current.id) {
                        handleSearchNavigate(event.shiftKey ? -1 : 1);
                      } else {
                        focusSearchResult(searchIndex);
                      }
                    }
                    if (event.key === 'Escape') {
                      setSearchQuery('');
                      event.currentTarget.blur();
                    }
                  }}
                  placeholder="Buscar (Ctrl+K)"
                  className="h-9 w-[220px] rounded-md border-border/80 bg-background pl-8 text-sm"
                />
              </div>
              {searchQuery && (
                <>
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {searchMatches.length > 0 ? `${searchIndex + 1}/${searchMatches.length}` : '0'}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => handleSearchNavigate(-1)}
                    disabled={searchMatches.length === 0}
                    title="Resultado anterior"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => handleSearchNavigate(1)}
                    disabled={searchMatches.length === 0}
                    title="Próximo resultado"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="secondary"
                  size="sm"
                  className={cn(
                    'h-9 rounded-md border border-border/80 bg-background px-3 text-sm font-medium text-foreground shadow-sm hover:bg-muted/50',
                    filterActive && 'border-primary/60 text-primary',
                  )}
                >
                  <Filter className="mr-2 h-4 w-4" />
                  Filtro
                  {filterActive && (
                    <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-[11px]">
                      {departmentFilterIds.size}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 p-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">Filtrar por departamento</h3>
                  {filterActive && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => setDepartmentFilterIds(new Set())}
                    >
                      <X className="mr-1 h-3 w-3" />
                      Limpar
                    </Button>
                  )}
                </div>
                {departamentos.length === 0 ? (
                  <p className="mt-3 text-xs text-muted-foreground">Nenhum departamento cadastrado.</p>
                ) : (
                  <div className="mt-3 max-h-56 space-y-2 overflow-y-auto pr-1">
                    {departamentos.map((departamento) => (
                      <label key={departamento.id} className="flex cursor-pointer items-center gap-2 text-sm">
                        <Checkbox
                          checked={departmentFilterIds.has(departamento.id)}
                          onCheckedChange={() =>
                            setDepartmentFilterIds((current) => {
                              const next = new Set(current);
                              if (next.has(departamento.id)) next.delete(departamento.id);
                              else next.add(departamento.id);
                              return next;
                            })
                          }
                        />
                        <span className="truncate">{departamento.nome}</span>
                      </label>
                    ))}
                  </div>
                )}
                <div className="mt-4 flex items-center justify-between gap-2 border-t pt-3">
                  <Label htmlFor="org-hide-filtered" className="text-xs text-muted-foreground">
                    Ocultar não correspondentes
                  </Label>
                  <Switch id="org-hide-filtered" checked={hideFiltered} onCheckedChange={setHideFiltered} />
                </div>
              </PopoverContent>
            </Popover>

            <Button
              variant="default"
              size="sm"
              className="h-8 gap-2 border border-primary/50 bg-primary px-3 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
              onClick={saveBlockedByConflict ? () => setSaveConflictOpen(true) : saveCanvas}
              disabled={!canEditCanvas || isSavingCanvas || isLoadingCanvas}
              title={saveBlockedByConflict ? 'Conflito de versão — clique para revisar' : 'Salvar (Ctrl+S)'}
            >
              <Save className="h-4 w-4" />
              Salvar
            </Button>
          </div>
        </div>

        {!isLoadingCanvas && (
          <div className="mt-2 flex items-center gap-4 font-semibold">
            {!canEditCanvas && <Badge className="border-border bg-muted/60 text-foreground hover:bg-muted/60">Somente leitura</Badge>}
            {currentOrganograma?.share_token && <span className="text-xs text-muted-foreground">Link público ativo</span>}
            <p className={cn('text-xs text-muted-foreground', dirty && !autoSaveDisabled && !saveBlockedByConflict && 'text-amber-600', (autoSaveDisabled || saveBlockedByConflict) && 'font-medium text-destructive')}>
            {saveBlockedByConflict
              ? 'Conflito de versão: recarregue para continuar salvando'
              : isSavingCanvas
                ? 'Salvando…'
                : autoSaveDisabled
                  ? 'Auto-save desativado — salve manualmente ou recarregue a página'
                  : dirty
                    ? 'Alterações não salvas'
                    : lastSavedAt
                      ? `Salvo às ${lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                      : 'Sem alterações pendentes'}
            </p>
          </div>
        )}
      </div>

      <div ref={workspaceRef} className="relative overflow-hidden rounded-3xl border border-border bg-background shadow-sm fullscreen:rounded-none fullscreen:border-0">
        <div className="flex flex-col gap-3 border-b border-border bg-[radial-gradient(circle_at_top_left,hsl(var(--muted)/0.65),transparent_32%),linear-gradient(135deg,hsl(var(--card)),hsl(var(--background)))] px-4 py-3 text-foreground">
          <div className="-mx-4 -my-3 flex items-center gap-1 overflow-x-auto px-4 py-1.5">
            {canEditCanvas && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={alternarPaleta}>
                    {paletteOpen ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeftOpen className="h-4 w-4" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{paletteOpen ? 'Ocultar painel Adicionar' : 'Mostrar painel Adicionar'}</TooltipContent>
              </Tooltip>
            )}

            <div className="mx-1 h-5 w-px shrink-0 bg-border" />

            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleExportPng}>
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Exportar PNG</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleExportPdf}>
                  <FileText className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Exportar PDF</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleOpenHistorico}>
                  <History className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Histórico</TooltipContent>
            </Tooltip>
            {canEditCanvas && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setIsImportDialogOpen(true)}>
                    <Upload className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Importar estrutura</TooltipContent>
              </Tooltip>
            )}

            <div className="mx-1 h-5 w-px shrink-0 bg-border" />

            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => fitView({ duration: 250, padding: 0.16 })}>
                  <Scan className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Enquadrar visualização</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={toggleFullscreen}>
                  <Maximize2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Tela cheia</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={snapColumnsTogether}>
                  <Columns3 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Colar colunas</TooltipContent>
            </Tooltip>
            {canEditCanvas && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setIsLayoutConfirmOpen(true)}>
                    <Wand2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Organizar automaticamente</TooltipContent>
              </Tooltip>
            )}

            {canManageOrganograma && (
              <>
                <div className="mx-1 h-5 w-px shrink-0 bg-border" />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleOpenPermissoes}>
                      <Shield className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Permissões</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setTentouSalvarMeta(false); setIsMetaDialogOpen(true); }}>
                      <Settings2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Metadados</TooltipContent>
                </Tooltip>
                {currentOrganograma?.share_token ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleRevokeShareLink}>
                        <Unlink className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Revogar link</TooltipContent>
                  </Tooltip>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleGenerateShareLink}>
                        <Link2 className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Gerar link</TooltipContent>
                  </Tooltip>
                )}
              </>
            )}

            <div className="mx-1 h-5 w-px shrink-0 bg-border" />

            <Popover open={howToOpen} onOpenChange={setHowToOpen}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <HelpCircle className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent>Como usar</TooltipContent>
              </Tooltip>
              <PopoverContent align="start" className="w-[26rem] p-3">
                <div className="flex flex-col gap-2">
                  {HOW_TO_STEPS.map((step, index) => (
                    <div key={step} className="flex items-start gap-2 rounded-md bg-muted/40 px-2.5 py-2">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[11px] font-semibold text-primary">
                        {index + 1}
                      </span>
                      <span className="text-xs leading-snug text-muted-foreground">{step}</span>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <div className="relative flex" style={{ height: canvasHeight }}>
          {canEditCanvas && paletteOpen && (
            <aside className="w-48 shrink-0 overflow-y-auto border-r bg-background">
              <div className="border-b px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Adicionar
              </div>
              <div className="space-y-1.5 p-2">
                <PaletteItem kind="column" label="Coluna" icon={<Columns3 className="h-4 w-4" />} onClick={addColumn} />
                <PaletteItem kind="department" label="Departamento" icon={<Building2 className="h-4 w-4" />} onClick={() => addCard('department')} />
                <PaletteItem kind="person" label="Pessoa" icon={<UserRound className="h-4 w-4" />} onClick={() => addCard('person')} />
                <PaletteItem kind="text" label="Texto" icon={<TypeIcon className="h-4 w-4" />} onClick={addText} />
                <PaletteItem kind="annotation" label="Anotação" icon={<StickyNote className="h-4 w-4" />} onClick={addAnnotation} />
                <PaletteItem kind="figure" label="Figura" icon={<Forklift className="h-4 w-4" />} onClick={() => addFigure()} />
                <PaletteItem kind="shape" label="Formas" icon={<Shapes className="h-4 w-4" />} onClick={() => addShape('square')} />
              </div>
              <p className="px-3 pb-3 text-[11px] text-muted-foreground">
                Arraste um item para o canvas, ou clique para adicionar no centro.
              </p>
            </aside>
          )}
          <main ref={exportAreaRef} className="h-full flex-1 bg-[#f8f4ea] dark:bg-muted/30">
            <ReactFlow
              nodes={decoratedNodes}
              edges={decoratedEdges}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              onNodesChange={handleNodesChangeWithGuides}
              onEdgesChange={onEdgesChange}
              onConnect={canEditCanvas ? handleConnect : undefined}
              onConnectStart={handleConnectStart}
              onConnectEnd={handleConnectEnd}
              onReconnect={canEditCanvas ? handleReconnect : undefined}
              onReconnectStart={canEditCanvas ? handleReconnectStart : undefined}
              onReconnectEnd={canEditCanvas ? handleReconnectEnd : undefined}
              edgesReconnectable={canEditCanvas}
              isValidConnection={isValidConnection}
              onEdgeDoubleClick={canEditCanvas ? handleEdgeDoubleClick : undefined}
              onNodeClick={clearFigureFocus}
              onPaneClick={clearFigureFocus}
              onNodeContextMenu={openNodeContextMenu}
              onEdgeContextMenu={openEdgeContextMenu}
              onPaneContextMenu={openPaneContextMenu}
              onNodeDragStart={handleNodeDragStart}
              onNodeDragStop={handleNodeDragStop}
              onSelectionChange={handleSelectionChange}
              onSelectionStart={() => {
                isBoxSelectingRef.current = true;
              }}
              onSelectionEnd={() => {
                isBoxSelectingRef.current = false;
              }}
              onDrop={canEditCanvas ? handlePaneDrop : undefined}
              onDragOver={(event) => {
                event.preventDefault();
                event.dataTransfer.dropEffect = 'copy';
              }}
              fitView
              snapToGrid
              snapGrid={[GRID_SIZE, GRID_SIZE]}
              minZoom={0.25}
              maxZoom={1.8}
              selectionOnDrag={interactionMode === 'select'}
              selectionMode={SelectionMode.Partial}
              multiSelectionKeyCode={['Control', 'Meta']}
              // Delete/Backspace são tratados no listener próprio (que registra histórico).
              deleteKeyCode={null}
              panOnDrag={interactionMode === 'select' ? [1, 2] : true}
              proOptions={{ hideAttribution: true }}
            >
              <Background variant={BackgroundVariant.Dots} gap={18} size={1} color="#b7aa92" />
              <AlignmentGuides horizontal={helperLineH} vertical={helperLineV} />
              <Controls position="bottom-left" showZoom showFitView showInteractive={false} />
              <MiniMap
                position="bottom-right"
                pannable
                zoomable
                nodeColor={(node) => {
                  const data = (node.data ?? {}) as Partial<OrgMvpColumnData & OrgMvpCardData & OrgMvpFigureData>;
                  if (node.type === 'column') return typeof data.color === 'string' ? data.color : '#E0F2FE';
                  if (node.type === 'figure') return typeof data.color === 'string' ? data.color : '#EA580C';
                  return typeof data.color === 'string' ? data.color : '#2563EB';
                }}
                maskColor="rgba(68, 64, 60, 0.12)"
              />
              {(isLoadingCanvas || !canEditCanvas) && (
                <Panel position="top-center" className="hidden rounded-full border border-border bg-background/90 px-3 py-1.5 text-xs text-muted-foreground shadow-sm backdrop-blur sm:block">
                  {isLoadingCanvas ? 'Carregando dados...' : 'Canvas público/somente leitura'}
                </Panel>
              )}
              {canEditCanvas && selectedCount >= 2 && (
                <Panel
                  position="bottom-center"
                  className="mb-1 flex items-center gap-1 rounded-full border border-border bg-background/95 px-2 py-1.5 shadow-lg backdrop-blur"
                >
                  <span className="px-1.5 text-xs font-medium text-muted-foreground">{selectedCount} itens</span>
                  <span className="mx-0.5 h-5 w-px bg-border" />
                  {ALIGN_ACTIONS.map(({ mode, label, icon: Icon }) => (
                    <Tooltip key={mode}>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => alignSelected(mode)}
                          aria-label={label}
                        >
                          <Icon className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{label}</TooltipContent>
                    </Tooltip>
                  ))}
                  <span className="mx-0.5 h-5 w-px bg-border" />
                  {DISTRIBUTE_ACTIONS.map(({ axis, label, icon: Icon }) => (
                    <Tooltip key={axis}>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            disabled={selectedCount < 3}
                            onClick={() => distributeSelected(axis)}
                            aria-label={label}
                          >
                            <Icon className="h-4 w-4" />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {selectedCount < 3 ? `${label} (precisa de 3+ itens)` : label}
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </Panel>
              )}
              <Panel position="top-left" className="flex gap-2 lg:hidden">
                {canEditCanvas && (
                  <Button size="sm" variant="secondary" className="h-9 border border-border bg-background shadow-sm" onClick={() => setMobilePaletteOpen(true)}>
                    <Plus className="mr-1.5 h-4 w-4" />
                    Adicionar
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="secondary"
                  className="h-9 border border-border bg-background shadow-sm"
                  onClick={() => setMobilePropsOpen(true)}
                  disabled={!selectedNode && !selectedEdge}
                >
                  <Settings2 className="mr-1.5 h-4 w-4" />
                  Propriedades
                </Button>
              </Panel>
            </ReactFlow>
          </main>

          <div
            role="separator"
            aria-orientation="horizontal"
            title="Arraste para redimensionar o canvas"
            onMouseDown={handleCanvasResizeStart}
            className="group absolute inset-x-0 bottom-0 z-30 flex h-3 cursor-ns-resize items-center justify-center"
          >
            <div className="h-1 w-16 rounded-full bg-border transition-colors group-hover:bg-primary/60" />
          </div>

          {/* Propriedades: só ocupa espaço quando algo está selecionado (coluna não é reservada
              o tempo todo), mas agora encaixada no layout como uma barra lateral de verdade —
              igual ao painel do Fluxograma (`border-l`) — em vez de flutuar sobre o canvas como
              um popup. O visual (border-l/bg-background/p-4) vem do próprio <PropertiesPanel>. */}
          {(selectedNode || selectedEdge) && (
            <div
              className="relative hidden h-full shrink-0 overflow-y-auto bg-background lg:block"
              style={{ width: propertiesPanelWidth }}
            >
              <div
                role="separator"
                aria-orientation="vertical"
                title="Arraste para redimensionar o painel"
                onMouseDown={handlePropertiesResizeStart}
                className="group absolute inset-y-0 left-0 z-10 flex w-3 -translate-x-1/2 cursor-ew-resize items-center justify-center"
              >
                <div className="h-16 w-1 rounded-full bg-border transition-colors group-hover:bg-primary/60" />
              </div>
              <PropertiesPanel
                selectedNode={selectedNode}
                selectedEdge={selectedEdge}
                onChange={handleDataChange}
                onChangeEdge={handleEdgeUpdate}
                onResize={handleNodeSizeChange}
                onChangeLayer={changeNodeLayer}
                figurasEmpresaId={currentOrganograma?.empresa_id}
                usuarios={usuarios}
                departamentos={departamentos}
                cronogramas={cronogramas}
                figureFocus={figureFocus}
                onExitFigureFocus={clearFigureFocus}
                onOpenFigureFocus={handleFigureClick}
                open
                onToggleOpen={() => {
                  setSelectedNodeId(null);
                  setSelectedEdgeId(null);
                }}
              />
            </div>
          )}
        </div>
      </div>

      <CanvasContextMenu ctx={ctxMenu} onClose={() => setCtxMenu(null)} buildItems={buildContextItems} />

      <Sheet open={mobilePaletteOpen} onOpenChange={setMobilePaletteOpen}>
        <SheetContent side="left" className="w-[290px]">
          <SheetHeader>
            <SheetTitle>Adicionar ao canvas</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-2">
            <PaletteItem kind="column" label="Coluna" icon={<Columns3 className="h-4 w-4" />} onClick={() => { addColumn(); setMobilePaletteOpen(false); }} />
            <PaletteItem kind="department" label="Departamento" icon={<Building2 className="h-4 w-4" />} onClick={() => { addCard('department'); setMobilePaletteOpen(false); }} />
            <PaletteItem kind="person" label="Pessoa" icon={<UserRound className="h-4 w-4" />} onClick={() => { addCard('person'); setMobilePaletteOpen(false); }} />
            <PaletteItem kind="figure" label="Figura" icon={<Forklift className="h-4 w-4" />} onClick={() => { addFigure(); setMobilePaletteOpen(false); }} />
            <PaletteItem kind="text" label="Texto" icon={<TypeIcon className="h-4 w-4" />} onClick={() => { addText(); setMobilePaletteOpen(false); }} />
            <PaletteItem kind="annotation" label="Anotação" icon={<StickyNote className="h-4 w-4" />} onClick={() => { addAnnotation(); setMobilePaletteOpen(false); }} />
          </div>
          <p className="mt-6 text-xs text-muted-foreground">
            O item é adicionado ao centro do canvas — arraste para posicionar.
          </p>
        </SheetContent>
      </Sheet>

      <Sheet open={mobilePropsOpen} onOpenChange={setMobilePropsOpen}>
        <SheetContent side="right" className="w-[320px] overflow-y-auto p-0 sm:w-[360px]">
          <SheetHeader className="sr-only">
            <SheetTitle>Propriedades</SheetTitle>
          </SheetHeader>
          <PropertiesPanel
            selectedNode={selectedNode}
            selectedEdge={selectedEdge}
            onChange={handleDataChange}
            onChangeEdge={handleEdgeUpdate}
            onResize={handleNodeSizeChange}
            onChangeLayer={changeNodeLayer}
            figurasEmpresaId={currentOrganograma?.empresa_id}
            usuarios={usuarios}
            departamentos={departamentos}
            cronogramas={cronogramas}
            figureFocus={figureFocus}
            onExitFigureFocus={clearFigureFocus}
            onOpenFigureFocus={handleFigureClick}
            open
            onToggleOpen={() => setMobilePropsOpen(false)}
          />
        </SheetContent>
      </Sheet>

      <Dialog open={templateChooserOpen} onOpenChange={setTemplateChooserOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Como você quer começar?</DialogTitle>
            <DialogDescription>
              Comece do zero ou parta de um exemplo pronto. O exemplo é só um ponto de partida —
              você pode editar, renomear e excluir tudo depois.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            {organogramaTemplates.map((template) => (
              <button
                key={template.key}
                type="button"
                className="flex items-start gap-3 rounded-2xl border border-border bg-background p-4 text-left transition hover:border-primary/50 hover:shadow-md"
                onClick={() => applyTemplate(template)}
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground">
                  <LayoutTemplate className="h-5 w-5" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold">{template.nome}</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">{template.descricao}</span>
                </span>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateChooserOpen(false)}>
              Começar em branco
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={saveConflictOpen} onOpenChange={setSaveConflictOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Organograma desatualizado</DialogTitle>
            <DialogDescription>
              Este organograma foi alterado por outra pessoa ou em outra aba. Para evitar sobrescrever
              mudanças mais novas, o salvamento foi pausado até você recarregar a versão atual.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveConflictOpen(false)}>
              Revisar antes
            </Button>
            <Button onClick={handleReloadAfterConflict}>Recarregar versão atual</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isMetaDialogOpen} onOpenChange={setIsMetaDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar organograma</DialogTitle>
            <DialogDescription>
              Ajuste os metadados principais sem sair do canvas.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                required
                aria-invalid={tentouSalvarMeta && !metaForm.nome.trim()}
                value={metaForm.nome}
                onChange={(event) => setMetaForm((current) => ({ ...current, nome: event.target.value }))}
                className={
                  tentouSalvarMeta && !metaForm.nome.trim()
                    ? 'border-destructive focus-visible:ring-destructive'
                    : undefined
                }
              />
              {tentouSalvarMeta && !metaForm.nome.trim() && (
                <p className="text-xs text-destructive">Informe o nome do organograma.</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Descrição</Label>
              <Textarea rows={4} value={metaForm.descricao} onChange={(event) => setMetaForm((current) => ({ ...current, descricao: event.target.value }))} />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <TipoOrganogramaSelect
                  value={metaForm.tipo}
                  onChange={(value) => setMetaForm((current) => ({ ...current, tipo: value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={metaForm.status} onValueChange={(value: Organograma['status']) => setMetaForm((current) => ({ ...current, status: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rascunho">Rascunho</SelectItem>
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="arquivado">Arquivado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMetaDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveMeta} disabled={!metaForm.nome.trim()}>Salvar metadados</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportOrganogramaDialog
        open={isImportDialogOpen}
        onOpenChange={setIsImportDialogOpen}
        onConfirm={handleConfirmImport}
      />

      <Dialog open={isHistoricoDialogOpen} onOpenChange={setIsHistoricoDialogOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Histórico do organograma</DialogTitle>
          </DialogHeader>
          <div className="max-h-[65vh] space-y-3 overflow-y-auto">
            {historico.length === 0 ? (
              <div className="rounded-lg border border-dashed bg-muted/20 p-6 text-center text-sm text-muted-foreground">
                Nenhuma alteração relevante registrada ainda.
              </div>
            ) : (
              historico.map((item) => (
                <div key={item.id} className="rounded-lg border p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline">{item.acao}</Badge>
                      <Badge variant="secondary">{item.entidade}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">{formatDateTime(item.createdAt)}</div>
                  </div>
                  <div className="mt-2 font-medium">{item.descricao}</div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    {item.usuario_nome} • {item.usuario_email}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isPermissoesDialogOpen} onOpenChange={setIsPermissoesDialogOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Permissões do organograma</DialogTitle>
          </DialogHeader>
          <div className="max-h-[65vh] space-y-3 overflow-y-auto">
            <div className="rounded-lg border border-dashed bg-muted/20 p-4 text-sm text-muted-foreground">
              Quando você salvar permissões aqui, elas passam a controlar leitura e edição para usuários comuns.
            </div>
            {usuarios.map((usuario) => {
              const selectedRole = permissoes.find((item) => item.usuario_id === usuario.id)?.role || 'none';
              return (
                <div key={usuario.id} className="grid items-center gap-3 rounded-lg border p-4 md:grid-cols-[1fr_220px]">
                  <div>
                    <div className="font-medium">{usuario.nome}</div>
                    <div className="text-sm text-muted-foreground">{usuario.email}</div>
                  </div>
                  <Select value={selectedRole} onValueChange={(value: 'none' | 'owner' | 'editor' | 'viewer') => handleChangePermissao(usuario.id, value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sem permissão explícita" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sem permissão explícita</SelectItem>
                      <SelectItem value="viewer">Leitor</SelectItem>
                      <SelectItem value="editor">Editor</SelectItem>
                      <SelectItem value="owner">Dono</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPermissoesDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSavePermissoes}>Salvar permissões</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pendingConnectionDraft} onOpenChange={(open) => !open && setPendingConnectionDraft(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>O que você quer criar?</DialogTitle>
            <DialogDescription>
              Escolha o tipo do novo componente para criar e conectar automaticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Button type="button" variant="outline" className="h-20 flex-col gap-2" onClick={() => createConnectedNodeFromDraft('figure')}>
              <Forklift className="h-5 w-5" />
              Figura
            </Button>
            <Button type="button" variant="outline" className="h-20 flex-col gap-2" onClick={() => createConnectedNodeFromDraft('department')}>
              <Building2 className="h-5 w-5" />
              Departamento
            </Button>
            <Button type="button" variant="outline" className="h-20 flex-col gap-2" onClick={() => createConnectedNodeFromDraft('person')}>
              <UserRound className="h-5 w-5" />
              Pessoa
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isLayoutConfirmOpen} onOpenChange={setIsLayoutConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Organizar automaticamente?</AlertDialogTitle>
            <AlertDialogDescription>
              Os cards conectados por ligações diretas serão reposicionados em árvore hierárquica.
              Colunas, figuras e cards sem conexão mantêm a posição. Você pode desfazer com Ctrl+Z.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setIsLayoutConfirmOpen(false);
                applyOrgLayout();
              }}
            >
              Organizar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingEdgeDeleteId} onOpenChange={(open) => !open && setPendingEdgeDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover conexão?</AlertDialogTitle>
            <AlertDialogDescription>
              Essa ação remove a linha entre os componentes. Você ainda pode desfazer depois.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmEdgeDelete}>Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ShapeGlyph({ shape, dashed }: { shape: OrgMvpShapeKind; dashed?: boolean }) {
  if (shape === 'circle') return <Circle className="h-4 w-4" />;
  if (shape === 'square') return <Square className="h-4 w-4" />;
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4">
      <line
        x1="1"
        y1="8"
        x2="15"
        y2="8"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeDasharray={dashed ? '3 3' : undefined}
      />
    </svg>
  );
}

/** Controle "− número +" estilo Word. `value === null` mostra o campo vazio (valores mistos). */
function NumberStepper({
  value,
  resetKey,
  disabled,
  title,
  decreaseLabel,
  increaseLabel,
  onStep,
  onCommit,
}: {
  value: number | null;
  /** Muda para forçar o rascunho a voltar ao valor real (troca de seleção, por exemplo). */
  resetKey?: string | number;
  disabled: boolean;
  title: string;
  decreaseLabel: string;
  increaseLabel: string;
  onStep: (direction: 1 | -1) => void;
  onCommit: (value: number) => void;
}) {
  const [draft, setDraft] = useState(value === null ? '' : String(value));

  useEffect(() => {
    setDraft(value === null ? '' : String(value));
  }, [value, resetKey]);

  const commit = () => {
    const parsed = Number(draft);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setDraft(value === null ? '' : String(value));
      return;
    }
    onCommit(parsed);
  };

  return (
    <div className="flex h-8 items-center rounded-md border border-input bg-background" title={title}>
      <button
        type="button"
        className="flex h-full w-7 items-center justify-center rounded-l-md text-muted-foreground transition hover:bg-muted disabled:opacity-40"
        disabled={disabled}
        onClick={() => onStep(-1)}
        aria-label={decreaseLabel}
      >
        <Minus className="h-3.5 w-3.5" />
      </button>
      <input
        type="number"
        className="h-full w-12 border-x border-input bg-transparent text-center text-xs tabular-nums outline-none disabled:opacity-40 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        value={draft}
        placeholder="—"
        disabled={disabled}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            event.currentTarget.blur();
          }
        }}
      />
      <button
        type="button"
        className="flex h-full w-7 items-center justify-center rounded-r-md text-muted-foreground transition hover:bg-muted disabled:opacity-40"
        disabled={disabled}
        onClick={() => onStep(1)}
        aria-label={increaseLabel}
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/** Stepper de tamanho dos elementos selecionados na barra de ferramentas. */
function SizeStepper({
  width,
  count,
  disabled,
  onStep,
  onSetAbsolute,
}: {
  width: number | null;
  count: number;
  disabled: boolean;
  onStep: (direction: 1 | -1) => void;
  onSetAbsolute: (value: number) => void;
}) {
  return (
    <NumberStepper
      value={width}
      resetKey={count}
      disabled={disabled}
      title={count === 0
        ? 'Selecione um ou mais elementos para ajustar o tamanho'
        : count === 1
          ? 'Tamanho do elemento selecionado (px)'
          : `Tamanho dos ${count} elementos selecionados — "+"/"−" ajustam proporcionalmente`}
      decreaseLabel="Diminuir tamanho"
      increaseLabel="Aumentar tamanho"
      onStep={onStep}
      onCommit={onSetAbsolute}
    />
  );
}

function PaletteItem({
  kind,
  label,
  icon,
  onClick,
}: {
  kind: OrgMvpNodeKind | 'column' | 'figure' | 'text' | 'table' | 'annotation' | 'shape';
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="flex w-full items-center gap-3 rounded-2xl border border-border bg-background px-3 py-3 text-left text-sm font-medium text-foreground shadow-sm transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
      draggable
      onClick={onClick}
      onDragStart={(event) => {
        event.dataTransfer.setData('application/x-org-mvp-kind', kind);
        event.dataTransfer.effectAllowed = 'copy';
      }}
    >
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-muted text-muted-foreground">{icon}</span>
      {label}
    </button>
  );
}

type EdgeUpdatePayload = {
  stroke?: string;
  strokeWidth?: number;
  tipo?: OrgMvpEdgeTipo;
  edgeType?: 'smoothstep' | 'straight' | 'default';
};

function PropertiesPanel({
  selectedNode,
  selectedEdge,
  onChange,
  onChangeEdge,
  onResize,
  onChangeLayer,
  figurasEmpresaId,
  usuarios,
  departamentos,
  cronogramas,
  figureFocus,
  onExitFigureFocus,
  onOpenFigureFocus,
  open,
  onToggleOpen,
}: {
  selectedNode: OrgMvpNode | null;
  selectedEdge: Edge | null;
  onChange: (nodeId: string, updates: Partial<OrgMvpCardData | OrgMvpColumnData>) => void;
  onChangeEdge: (updates: EdgeUpdatePayload) => void;
  onResize: (nodeId: string, size: { width?: number; height?: number }) => void;
  onChangeLayer: (direction: 'front' | 'back') => void;
  /** Empresa do organograma: catálogo e envio de imagens da biblioteca de figuras da empresa. */
  figurasEmpresaId?: string | null;
  usuarios: Usuario[];
  departamentos: Departamento[];
  cronogramas: Cronograma[];
  /** Card cujo ícone foi clicado — abre o painel direto no seletor de ícone dele. */
  figureFocus: { nodeId: string; at: number } | null;
  onExitFigureFocus: () => void;
  /** Abre o seletor de ícone/figura do nó — mesmo destino do clique no ícone dentro do card. */
  onOpenFigureFocus: (nodeId: string) => void;
  open: boolean;
  onToggleOpen: () => void;
}) {
  const normalizeSourceType = (value: unknown): 'SYSTEM_USER' | 'ORG_ONLY' => {
    if (value === 'SYSTEM_USER' || value === 'ORG_ONLY') return value;
    return 'ORG_ONLY';
  };

  const normalizeFigure = (value: unknown, fallback: 'person' | 'building' | 'forklift'): OrgMvpFigure => {
    if (value && typeof value === 'object' && 'type' in value) {
      const figure = value as OrgMvpFigure;
      if (figure.type === 'upload' && typeof figure.url === 'string' && typeof figure.name === 'string') return figure;
      if (figure.type === 'catalog' && typeof figure.key === 'string') return figure;
    }
    return { type: 'catalog', key: fallback };
  };

  if (!open) {
    return (
      <aside className="flex flex-col items-center border-l border-border bg-background p-2">
        <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl bg-muted" onClick={onToggleOpen} title="Expandir propriedades">
          <ChevronLeft className="h-4 w-4" />
        </Button>
      </aside>
    );
  }

  if (!selectedNode) {
    if (selectedEdge) {
      const currentStroke = typeof selectedEdge.style?.stroke === 'string' ? selectedEdge.style.stroke : '#334155';
      const currentTipo = getEdgeTipo(selectedEdge);
      const currentEdgeType = selectedEdge.type === 'smoothstep' || selectedEdge.type === 'straight' ? selectedEdge.type : 'default';
      const currentWidth = typeof selectedEdge.style?.strokeWidth === 'number' ? selectedEdge.style.strokeWidth : 2.2;
      return (
        <aside className="border-l border-border bg-background p-4 text-foreground">
          <header className="flex items-center gap-2">
            <h2 className="text-sm font-semibold">Conexão selecionada</h2>
            <Button variant="ghost" size="icon" className="ml-auto h-8 w-8" onClick={onToggleOpen} title="Recolher propriedades">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </header>
          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label>Tipo de relação</Label>
              <Select value={currentTipo} onValueChange={(value: OrgMvpEdgeTipo) => onChangeEdge({ tipo: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EDGE_TIPOS.map((tipo) => (
                    <SelectItem key={tipo} value={tipo}>
                      {EDGE_TIPO_LABELS[tipo]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Relações pontilhada, staff e projeto ficam fora da hierarquia (contadores e colapso).
              </p>
            </div>
            <div className="space-y-2">
              <Label className="mb-1.5 block">Forma da linha</Label>
              <ToggleGroup
                type="single"
                value={currentEdgeType}
                onValueChange={(value: 'smoothstep' | 'straight' | 'default') =>
                  value && onChangeEdge({ edgeType: value })
                }
                className="justify-start gap-1"
              >
                <ToggleGroupItem
                  value="smoothstep"
                  className="flex-1 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                >
                  Ortogonal
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="default"
                  className="flex-1 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                >
                  Curva
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="straight"
                  className="flex-1 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                >
                  Reta
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
            <div className="space-y-2">
              <Label>Espessura (px)</Label>
              <NumberStepper
                value={currentWidth}
                resetKey={selectedEdge.id}
                disabled={false}
                title={`Espessura da linha, de ${MIN_STROKE_WIDTH} a ${MAX_STROKE_WIDTH}px`}
                decreaseLabel="Diminuir espessura da linha"
                increaseLabel="Aumentar espessura da linha"
                onStep={(direction) => onChangeEdge({ strokeWidth: clampStrokeWidth(currentWidth + direction) })}
                onCommit={(value) => onChangeEdge({ strokeWidth: clampStrokeWidth(value) })}
              />
            </div>
            <div className="space-y-2">
              <Label>Cor da conexão</Label>
              <Input
                type="color"
                value={normalizeColorInput(currentStroke)}
                onChange={(event) => onChangeEdge({ stroke: event.target.value })}
                className="h-10 w-full cursor-pointer"
              />
            </div>
            <div className="grid grid-cols-6 gap-2">
              {colorPresets.map((preset) => (
                <button
                  key={`edge-${preset}`}
                  type="button"
                  className={cn(
                    'h-8 w-8 rounded-md border-2 transition',
                    currentStroke === preset ? 'border-white shadow-md ring-2 ring-stone-900/15' : 'border-transparent',
                  )}
                  style={{ backgroundColor: preset }}
                  onClick={() => onChangeEdge({ stroke: preset })}
                  title={preset}
                  aria-label={`Usar cor ${preset}`}
                />
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Dica: arraste a ponta da linha para reconectar em outro card. Duplo clique remove a conexão.
            </p>
          </div>
        </aside>
      );
    }

    return (
      <aside className="border-l border-border bg-background p-4 text-foreground">
        <header className="flex items-center gap-2">
          <h2 className="text-sm font-semibold">Propriedades</h2>
          <Button variant="ghost" size="icon" className="ml-auto h-8 w-8" onClick={onToggleOpen} title="Recolher propriedades">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </header>
        <div className="mt-8 rounded-2xl border border-dashed border-border bg-muted/30 p-4 text-center text-sm text-muted-foreground">
          Selecione uma coluna ou card para editar textos e cores.
        </div>
      </aside>
    );
  }

  const isColumn = selectedNode.type === 'column';
  const isFigure = selectedNode.type === 'figure';
  const isShape = selectedNode.type === 'shape';
  const cardData = selectedNode.data as OrgMvpCardData;
  const columnData = selectedNode.data as OrgMvpColumnData;
  const figureData = selectedNode.data as OrgMvpFigureData;
  const safeCardData: OrgMvpCardData = {
    kind: cardData?.kind === 'department' ? 'department' : 'person',
    label: typeof cardData?.label === 'string' ? cardData.label : '',
    subtitle: typeof cardData?.subtitle === 'string' ? cardData.subtitle : '',
    description: typeof cardData?.description === 'string' ? cardData.description : '',
    sourceType: normalizeSourceType(cardData?.sourceType),
    usuarioId: typeof cardData?.usuarioId === 'string' ? cardData.usuarioId : null,
    departamentoId: typeof cardData?.departamentoId === 'string' ? cardData.departamentoId : null,
    color: typeof cardData?.color === 'string' ? cardData.color : '#2563EB',
    textColor: typeof cardData?.textColor === 'string' ? cardData.textColor : '#0F172A',
    figure: normalizeFigure(cardData?.figure, cardData?.kind === 'department' ? 'building' : 'person'),
  };
  // Foto de perfil no card: vem como figura de upload SEM caminho no storage (não é imagem enviada ao card).
  const mostraFotoDoUsuario = safeCardData.figure.type === 'upload' && !safeCardData.figure.storagePath;
  const figuraPadraoPessoa = safeCardData.figure.type === 'catalog' && safeCardData.figure.key === 'person';
  const ocultarFoto = cardData?.ocultarFoto === true;
  const figuraDoUsuario = (usuarioId: string | null): OrgMvpFigure => {
    const usuario = usuarioId ? usuarios.find((u) => u.id === usuarioId) : undefined;
    return usuario?.avatar_url
      ? { type: 'upload', url: usuario.avatar_url, name: usuario.nome }
      : { type: 'catalog', key: 'person' };
  };
  const safeFigureData: OrgMvpFigureData = {
    label: typeof figureData?.label === 'string' ? figureData.label : 'Figura',
    color: typeof figureData?.color === 'string' ? figureData.color : '#EA580C',
    showBackground: figureData?.showBackground !== false,
    figure: normalizeFigure(figureData?.figure, 'forklift'),
  };
  const selectedNodeSize = nodeSizeOf(selectedNode);
  const safeColumnData: OrgMvpColumnData = {
    title: typeof columnData?.title === 'string' ? columnData.title : 'Coluna',
    color: typeof columnData?.color === 'string' ? columnData.color : '#E0F2FE',
    orientation: columnData?.orientation === 'horizontal' ? 'horizontal' : 'vertical',
    titlePosition: columnData?.titlePosition,
    readonly: columnData?.readonly,
  };
  const columnTitlePosition = resolveColumnTitlePosition(safeColumnData);
  const shapeData = selectedNode.data as OrgMvpShapeData;
  const safeShapeData: OrgMvpShapeData = {
    shape: shapeData?.shape === 'circle' || shapeData?.shape === 'line' ? shapeData.shape : 'square',
    dashed: shapeData?.dashed === true,
    color: typeof shapeData?.color === 'string' ? shapeData.color : '#334155',
    fill: typeof shapeData?.fill === 'string' ? shapeData.fill : 'transparent',
    strokeWidth: typeof shapeData?.strokeWidth === 'number' ? shapeData.strokeWidth : 2,
  };

  // Item 18: o campo de título/nome é sempre o primeiro do formulário, seja qual for o tipo do nó.
  const titleField = isColumn ? (
    <div className="space-y-2">
      <Label>Título da coluna</Label>
      <Input
        value={safeColumnData.title}
        onChange={(event) => onChange(selectedNode.id, { title: event.target.value })}
      />
    </div>
  ) : isFigure ? (
    <div className="space-y-2">
      <Label>Nome interno</Label>
      <Input
        value={safeFigureData.label}
        onChange={(event) => onChange(selectedNode.id, { label: event.target.value })}
        placeholder="Ex: Empilhadeira 01"
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full justify-start gap-2 text-xs"
        onClick={() => onOpenFigureFocus(selectedNode.id)}
      >
        <ImagePlus className="h-4 w-4" />
        Personalizar figura/ícone
      </Button>
    </div>
  ) : isShape ? null : (
    <div className="space-y-2">
      <Label>Nome</Label>
      <Input
        value={safeCardData.label}
        onChange={(event) => onChange(selectedNode.id, { label: event.target.value })}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full justify-start gap-2 text-xs"
        onClick={() => onOpenFigureFocus(selectedNode.id)}
      >
        <ImagePlus className="h-4 w-4" />
        Personalizar figura/ícone
      </Button>
    </div>
  );

  // Clique na figura do card abre só o seletor de ícone — não o formulário inteiro. É o
  // caminho oposto ao clique no resto do card, que cai no painel normal abaixo.
  if (figureFocus && figureFocus.nodeId === selectedNode.id && !isColumn && !isShape) {
    return (
      <aside className="border-l border-border bg-background p-4 text-foreground">
        <Button variant="ghost" size="sm" className="mb-3 -ml-2 h-8 gap-1 px-2 text-xs" onClick={onExitFigureFocus}>
          <ChevronLeft className="h-4 w-4" />
          Voltar às propriedades
        </Button>
        <FigurePicker
          value={isFigure ? safeFigureData.figure : safeCardData.figure}
          onChange={(figure) => onChange(selectedNode.id, { figure })}
          empresaId={figurasEmpresaId}
        />
      </aside>
    );
  }

  return (
    <aside className="border-l border-border bg-background p-4 text-foreground">
      {/* Sem cabeçalho: o formulário começa no topo do painel. Excluir o item selecionado
          continua em Delete/Backspace e no menu de clique direito do canvas. */}
      <div className="space-y-4">
        {titleField}

        <SizeFields
          nodeId={selectedNode.id}
          width={selectedNodeSize.width}
          height={selectedNodeSize.height}
          onResize={onResize}
        />

        <div className="space-y-2">
          <Label>Camada</Label>
          <div className="grid grid-cols-2 gap-2">
            <Button type="button" variant="outline" size="sm" className="justify-start text-xs" onClick={() => onChangeLayer('front')}>
              <ArrowUpToLine className="mr-1.5 h-4 w-4 shrink-0" />
              Para frente
            </Button>
            <Button type="button" variant="outline" size="sm" className="justify-start text-xs" onClick={() => onChangeLayer('back')}>
              <ArrowDownToLine className="mr-1.5 h-4 w-4 shrink-0" />
              Para trás
            </Button>
          </div>
        </div>

        {isColumn ? (
          <>
            <div className="space-y-2">
              <Label>Posição do título</Label>
              <div className="grid grid-cols-3 gap-2">
                {COLUMN_TITLE_POSITION_OPTIONS.map((option) => (
                  <Button
                    key={option.value}
                    type="button"
                    size="sm"
                    variant={columnTitlePosition === option.value ? 'default' : 'outline'}
                    className="text-xs"
                    onClick={() => onChange(selectedNode.id, { titlePosition: option.value })}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                O nome da coluna aparece só na posição escolhida.
              </p>
            </div>
            <ColorPicker
              label="Cor da coluna"
              value={safeColumnData.color}
              onChange={(color) => onChange(selectedNode.id, { color })}
            />
            <div className="space-y-2">
              <Label>Orientação</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={safeColumnData.orientation !== 'horizontal' ? 'default' : 'outline'}
                  onClick={() => onChange(selectedNode.id, { orientation: 'vertical', titlePosition: 'top' })}
                >
                  Vertical
                </Button>
                <Button
                  type="button"
                  variant={safeColumnData.orientation === 'horizontal' ? 'default' : 'outline'}
                  onClick={() => {
                    onChange(selectedNode.id, { orientation: 'horizontal', titlePosition: 'left' });
                  }}
                >
                  Horizontal
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Horizontal funciona como uma raia para fluxos da esquerda para direita.
              </p>
            </div>
          </>
        ) : isFigure ? (
          <>
            <ColorPicker
              label="Cor"
              value={safeFigureData.color}
              onChange={(color) => onChange(selectedNode.id, { color })}
            />
            <div className="space-y-2">
              <Label>Fundo</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={!safeFigureData.showBackground ? 'default' : 'outline'}
                  onClick={() => onChange(selectedNode.id, { showBackground: false })}
                >
                  Sem fundo
                </Button>
                <Button
                  type="button"
                  variant={safeFigureData.showBackground ? 'default' : 'outline'}
                  onClick={() => onChange(selectedNode.id, { showBackground: true })}
                >
                  Com fundo
                </Button>
              </div>
            </div>
          </>
        ) : isShape ? (
          <>
            <div className="space-y-2">
              <Label>Forma</Label>
              <div className="grid grid-cols-3 gap-2">
                {SHAPE_OPTIONS.map((option) => (
                  <Button
                    key={option.shape}
                    type="button"
                    size="sm"
                    variant={safeShapeData.shape === option.shape ? 'default' : 'outline'}
                    className="flex-col gap-1 py-6 text-[11px]"
                    onClick={() => onChange(selectedNode.id, { shape: option.shape })}
                  >
                    <ShapeGlyph shape={option.shape} dashed={safeShapeData.dashed} />
                    {option.label}
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Estilo do traço</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={!safeShapeData.dashed ? 'default' : 'outline'}
                  onClick={() => onChange(selectedNode.id, { dashed: false })}
                >
                  Contínuo
                </Button>
                <Button
                  type="button"
                  variant={safeShapeData.dashed ? 'default' : 'outline'}
                  onClick={() => onChange(selectedNode.id, { dashed: true })}
                >
                  Pontilhado
                </Button>
              </div>
            </div>
            <ColorPicker
              label="Cor do traço"
              value={safeShapeData.color}
              onChange={(color) => onChange(selectedNode.id, { color })}
            />
            <div className="space-y-2">
              <Label>Espessura do traço (px)</Label>
              <NumberStepper
                value={safeShapeData.strokeWidth ?? MIN_STROKE_WIDTH}
                resetKey={selectedNode.id}
                disabled={false}
                title={`Espessura do traço, de ${MIN_STROKE_WIDTH} a ${MAX_STROKE_WIDTH}px`}
                decreaseLabel="Diminuir espessura do traço"
                increaseLabel="Aumentar espessura do traço"
                onStep={(direction) =>
                  onChange(selectedNode.id, {
                    strokeWidth: clampStrokeWidth((safeShapeData.strokeWidth ?? MIN_STROKE_WIDTH) + direction),
                  })
                }
                onCommit={(value) => onChange(selectedNode.id, { strokeWidth: clampStrokeWidth(value) })}
              />
            </div>
            {safeShapeData.shape !== 'line' && (
              <div className="space-y-2">
                <Label>Preenchimento</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={safeShapeData.fill === 'transparent' ? 'default' : 'outline'}
                    onClick={() => onChange(selectedNode.id, { fill: 'transparent' })}
                  >
                    Sem preenchimento
                  </Button>
                  <Input
                    type="color"
                    value={normalizeColorInput(safeShapeData.fill === 'transparent' ? safeShapeData.color : safeShapeData.fill || '')}
                    onChange={(event) => onChange(selectedNode.id, { fill: event.target.value })}
                    className="h-10 w-full cursor-pointer"
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {safeCardData.kind === 'person' ? (
              <>
                <div className="space-y-2">
                  <Label>Origem da pessoa</Label>
                  <Select
                    value={safeCardData.sourceType || 'ORG_ONLY'}
                    onValueChange={(value: 'SYSTEM_USER' | 'ORG_ONLY') =>
                      onChange(selectedNode.id, {
                        sourceType: value,
                        usuarioId: value === 'SYSTEM_USER' ? safeCardData.usuarioId || null : null,
                        // Deixou de ser usuário do sistema: a foto dele sai do card.
                        ...(value === 'ORG_ONLY' && mostraFotoDoUsuario ? { figure: { type: 'catalog', key: 'person' } as OrgMvpFigure } : {}),
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a origem" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SYSTEM_USER">Usuário do sistema</SelectItem>
                      <SelectItem value="ORG_ONLY">Pessoa exclusiva do organograma</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {safeCardData.sourceType === 'SYSTEM_USER' && (
                  <div className="space-y-2">
                    <Label>Usuário vinculado</Label>
                    <Select
                      value={safeCardData.usuarioId || 'none'}
                      onValueChange={(value) => {
                        if (value === 'none') {
                          onChange(selectedNode.id, {
                            usuarioId: null,
                            ...(mostraFotoDoUsuario ? { figure: { type: 'catalog', key: 'person' } as OrgMvpFigure } : {}),
                          });
                          return;
                        }
                        const user = usuarios.find((u) => u.id === value);
                        // Trocou o usuário: a foto acompanha (se o card usa a foto e ela não foi desligada).
                        const trocaFoto = !ocultarFoto && (mostraFotoDoUsuario || figuraPadraoPessoa)
                          ? { figure: figuraDoUsuario(value) }
                          : {};
                        onChange(selectedNode.id, {
                          usuarioId: value,
                          label: user?.nome || safeCardData.label,
                          ...trocaFoto,
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um usuário" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sem vínculo</SelectItem>
                        {usuarios.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {safeCardData.sourceType === 'SYSTEM_USER' && safeCardData.usuarioId && (mostraFotoDoUsuario || figuraPadraoPessoa) && (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <Label htmlFor="org-mostrar-foto">Mostrar foto do usuário</Label>
                      <Switch
                        id="org-mostrar-foto"
                        checked={!ocultarFoto}
                        onCheckedChange={(ligado) =>
                          onChange(
                            selectedNode.id,
                            ligado
                              ? { ocultarFoto: false, figure: figuraDoUsuario(safeCardData.usuarioId ?? null) }
                              : { ocultarFoto: true, figure: { type: 'catalog', key: 'person' } },
                          )
                        }
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Usa a foto do perfil, quando houver. Escolher outro ícone ou enviar uma imagem também substitui a foto.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-2">
                <Label>Departamento vinculado</Label>
                <Select
                  value={safeCardData.departamentoId || 'none'}
                  onValueChange={(value) => {
                    if (value === 'none') {
                      onChange(selectedNode.id, { departamentoId: null });
                      return;
                    }
                    const departamento = departamentos.find((d) => d.id === value);
                    onChange(selectedNode.id, {
                      departamentoId: value,
                      label: departamento?.nome || safeCardData.label,
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um departamento" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem vínculo</SelectItem>
                    {departamentos.map((departamento) => (
                      <SelectItem key={departamento.id} value={departamento.id}>
                        {departamento.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Projeto vinculado</Label>
              <Select
                value={cardData.cronogramaId || 'none'}
                onValueChange={(value) => {
                  if (value === 'none') {
                    onChange(selectedNode.id, { cronogramaId: null, cronogramaTitulo: null });
                    return;
                  }
                  const cronograma = cronogramas.find((c) => c.id === value);
                  onChange(selectedNode.id, {
                    cronogramaId: value,
                    cronogramaTitulo: cronograma?.titulo || null,
                  });
                }}
              >
                {/* asChild: o próprio botão vira o gatilho do Select — visual de botão outline
                    em vez do campo com aparência de select padrão. */}
                <SelectPrimitive.Trigger asChild>
                  <Button type="button" variant="outline" size="sm" className="w-full justify-start">
                    {cardData.cronogramaTitulo || 'Vincular'}
                  </Button>
                </SelectPrimitive.Trigger>
                <SelectContent>
                  <SelectItem value="none">Sem vínculo</SelectItem>
                  {cronogramas.map((cronograma) => (
                    <SelectItem key={cronograma.id} value={cronograma.id}>
                      {cronograma.titulo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Cargo / área</Label>
              <Input
                value={safeCardData.subtitle}
                onChange={(event) => onChange(selectedNode.id, { subtitle: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Observação</Label>
              <Textarea
                rows={4}
                value={safeCardData.description || ''}
                onChange={(event) => onChange(selectedNode.id, { description: event.target.value })}
              />
            </div>
            {/* Sem FigurePicker aqui: trocar o ícone é só clicando nele direto no card
                (abre o painel focado acima) — pedido do Lucas (08/09) pra tirar a duplicata. */}
            <ColorPicker
              label="Cor do card"
              value={safeCardData.color}
              onChange={(color) => onChange(selectedNode.id, { color })}
            />
          </>
        )}
      </div>
    </aside>
  );
}

/** Sincroniza nos dois sentidos: o rascunho local reseta sempre que o tamanho real do
 *  node muda (inclusive por arraste do NodeResizer), e digitar aqui redimensiona o node. */
function SizeFields({
  nodeId,
  width,
  height,
  onResize,
}: {
  nodeId: string;
  width: number;
  height: number;
  onResize: (nodeId: string, size: { width?: number; height?: number }) => void;
}) {
  const [draft, setDraft] = useState({ width: String(Math.round(width)), height: String(Math.round(height)) });

  useEffect(() => {
    setDraft({ width: String(Math.round(width)), height: String(Math.round(height)) });
  }, [nodeId, width, height]);

  const commit = (field: 'width' | 'height') => {
    const parsed = Number(draft[field]);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setDraft({ width: String(Math.round(width)), height: String(Math.round(height)) });
      return;
    }
    onResize(nodeId, { [field]: parsed });
  };

  return (
    <div className="space-y-2">
      <Label>Tamanho (px)</Label>
      <div className="grid grid-cols-2 gap-2">
        {(['width', 'height'] as const).map((field) => (
          <div key={field} className="space-y-1">
            <span className="text-xs text-muted-foreground">{field === 'width' ? 'Largura' : 'Altura'}</span>
            <Input
              type="number"
              min={MIN_NODE_SIZE}
              max={MAX_NODE_SIZE}
              value={draft[field]}
              onChange={(event) => setDraft((current) => ({ ...current, [field]: event.target.value }))}
              onBlur={() => commit(field)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  commit(field);
                }
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function FigurePicker({
  value,
  onChange,
  empresaId,
}: {
  value: OrgMvpFigure;
  onChange: (figure: OrgMvpFigure) => void;
  /** Empresa do organograma (biblioteca de figuras da empresa). */
  empresaId?: string | null;
}) {
  const normalizedValue: OrgMvpFigure = value?.type === 'upload' || value?.type === 'catalog'
    ? value
    : { type: 'catalog', key: 'forklift' };
  const [query, setQuery] = useState('');
  // Altura padrão 1,75x a antiga (max-h-56 = 224px), arrastável pela alça abaixo do catálogo.
  const [catalogHeight, setCatalogHeight] = useState(392);
  const catalogResizeRef = useRef<{ startY: number; startHeight: number } | null>(null);

  const handleCatalogResizeStart = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    catalogResizeRef.current = { startY: event.clientY, startHeight: catalogHeight };

    const onMouseMove = (moveEvent: MouseEvent) => {
      if (!catalogResizeRef.current) return;
      const delta = moveEvent.clientY - catalogResizeRef.current.startY;
      const next = Math.min(640, Math.max(160, catalogResizeRef.current.startHeight + delta));
      setCatalogHeight(next);
    };
    const onMouseUp = () => {
      catalogResizeRef.current = null;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [catalogHeight]);

  const normalizedQuery = normalizeSearchText(query.trim());
  const filteredCatalog = normalizedQuery
    ? figureCatalog.filter((item) => normalizeSearchText(item.label).includes(normalizedQuery))
    : figureCatalog;

  return (
    <div className="space-y-3">
      <div>
        <Label>Figura / ícone</Label>
        <p className="mt-1 text-xs text-muted-foreground">
          {figureCatalog.length} ícones disponíveis, ou uma imagem das figuras da empresa.
        </p>
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Buscar figura por nome..."
          className="h-8 pl-8 text-xs"
        />
      </div>

      <div className="relative">
        <div
          className="grid auto-rows-max content-start grid-cols-3 gap-2 overflow-y-auto rounded-2xl border border-border bg-muted/30 p-2 pb-4"
          style={{ height: catalogHeight }}
        >
        {filteredCatalog.length === 0 && (
          <p className="col-span-3 py-4 text-center text-xs text-muted-foreground">Nenhuma figura encontrada.</p>
        )}
        {filteredCatalog.map((item) => {
          const Icon = item.icon;
          const isSelected = normalizedValue.type === 'catalog' && normalizedValue.key === item.key;
          return (
            <button
              key={item.key}
              type="button"
              className={cn(
                'flex flex-col items-center gap-1 overflow-hidden rounded-xl border border-border bg-background px-1.5 py-2 text-[11px] text-muted-foreground shadow-sm transition hover:-translate-y-0.5 hover:border-primary/40 hover:text-foreground',
                isSelected && 'border-primary ring-2 ring-primary/20 text-foreground',
              )}
              onClick={() => onChange({ type: 'catalog', key: item.key })}
              title={item.label}
            >
              <Icon className="h-5 w-5 shrink-0 text-foreground" />
              <span className="w-full truncate text-center">{item.label}</span>
            </button>
          );
        })}
        </div>
        <div
          role="separator"
          aria-orientation="horizontal"
          title="Arraste para aumentar/diminuir o catálogo"
          onMouseDown={handleCatalogResizeStart}
          className="group absolute inset-x-0 bottom-0 flex h-3 cursor-ns-resize items-center justify-center"
        >
          <div className="h-1 w-10 rounded-full bg-border transition-colors group-hover:bg-primary/60" />
        </div>
      </div>

      {normalizedValue.type === 'upload' && (
        <div className="flex items-center gap-3 rounded-xl border border-border bg-background p-2">
          <img
            src={normalizedValue.url}
            alt={normalizedValue.name}
            className="h-10 w-10 shrink-0 rounded-lg border border-border bg-background object-contain"
          />
          <div className="min-w-0 flex-1">
            <p className="text-[11px] text-muted-foreground">Imagem em uso</p>
            <p className="truncate text-xs text-foreground">{normalizedValue.name}</p>
          </div>
        </div>
      )}

      {/* Envio de imagem vai para a biblioteca da empresa (2026-10-23) e fica disponível em todo organograma/fluxograma. */}
      <BibliotecaFiguras
        empresaId={empresaId}
        isSelecionada={(figura) => normalizedValue.type === 'upload' && normalizedValue.storagePath === figura.storage_path}
        onSelect={(figura) => onChange({
          type: 'upload',
          url: figura.url,
          name: figura.nome,
          storagePath: figura.storage_path,
          bucket: figura.bucket,
          mimeType: figura.mime_type,
        })}
      />
    </div>
  );
}

function ColorPicker({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (color: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-2">
        {colorPresets.map((color) => (
          <button
            key={color}
            type="button"
            className={cn(
              'h-8 w-8 rounded-full border-2 transition hover:scale-105',
              value === color ? 'border-white shadow-md ring-2 ring-stone-900/15' : 'border-transparent',
            )}
            style={{ backgroundColor: color }}
            onClick={() => onChange(color)}
            aria-label={`Usar cor ${color}`}
          />
        ))}
      </div>
      <Input type="color" value={normalizeColorInput(value)} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

function normalizeColorInput(color: string) {
  return /^#[0-9a-f]{6}$/i.test(color) ? color : '#2563EB';
}

function formatDateTime(value?: string) {
  if (!value) return 'Sem registro';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sem registro';
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function slugify(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'organograma';
}

const MemoOrganogramaMvpInner = memo(OrganogramaMvpInner);

export default function OrganogramaMvp() {
  return (
    <AppLayout>
      <ReactFlowProvider>
        <MemoOrganogramaMvpInner />
      </ReactFlowProvider>
    </AppLayout>
  );
}
